import {
  type FocusEvent,
  type MouseEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import PageTemplate from '../../components/PageTemplate/PageTemplate';
import { renderListTotals, SummaryAnswers } from '../../components/SummaryAnswers/SummaryAnswers';
import Download, { buildExportHtml } from '../../components/Download/Download';
import UnlockNotice from '../../components/UnlockNotice/UnlockNotice';
import Notification from '../../components/Notification/Notification';
import ContextTables from '../../components/ContextTables/ContextTables';
import config from '../../config';
import content from '../../content';
import { useAuthContext } from '../../auth/AuthContext';
import { useUiContext } from '../../context/UiContext';
import { useFormConfig } from '../../hooks/useFormConfig';
import { useFormData } from '../../hooks/useFormData';
import { Status, Query } from '../../types';
import {
  canEditApprovalBeforeClose,
  extractConditionalQuestions,
  getEnabledSubsections,
  hasIncompleteRequiredSubsections,
  getOverviewStatus,
  isApprovalPageName,
  isPageDisabled,
  isSubsectionNotRequired,
  isWorkflowOnlyPageName,
} from '../../utils/config-utils';
import { resolveSelectedFormId, setSelectedFormIdInSession } from '../../utils/form-id';
import { isPastLondon, formatLondonDateTime } from '../../utils/londonTime';
import { DeclarationWrapper } from '../OverviewScreen/Wrappers';
import type { FormConfig, Page, Question, Section, SelectOption } from '../../config/types';
import type {
  ApprovalRecord,
  ApprovalRequestRecord,
  ContextTable,
  FetchFormResponse,
} from '../../service/types';
import Fieldset from '../../components/form/Fieldset';
import Textarea from '../../components/form/Textarea';
import TextInputField from '../../components/form/TextInputField';
import SelectInputField from '../../components/form/SelectInputField';
import RadioGroup from '../../components/form/RadioGroup/RadioGroup';
import CheckboxGroup from '../../components/form/CheckboxGroup/CheckboxGroup';
import { DateInputField } from '../../components/form/DateInputField/DateInputField';
import { ShortDateInputField } from '../../components/form/DateInputField/ShortDateInputField';
import AutocompleteWrapper from '../../components/form/Autocomplete/AutocompleteWrapper';
import AthenaStaffLookup from '../../components/form/AthenaStaffLookup';
import OptionalLabel from '../../components/form/OptionalLabel';
import TableauLicenseKeys from '../../components/form/TableauLicenseKeys';
import ErrorSummary from '../../components/ErrorSummary/ErrorSummary';
import {
  approveForm,
  convertExportHtmlToPdf,
  downloadExportExcel,
  downloadExportRenderedHtml,
  fetchForm,
  unsubmitForm,
  updateForm,
} from '../../service/form';
import { fetchFormUsers } from '../../service/form';
import { isAdminPreviewMode, parseOptionalBoolValue } from '../../utils/admin-preview';
import { hasValue, validateField } from '../../utils/form-utils';
import { parseSafeHtml } from '../../utils/safe-html';
import { ApiError } from '../../utils/api-error';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const historyDownloadButtonGroupStyle = {
  display: 'flex',
  gap: '6px',
  alignItems: 'center',
} as const;

const historyDownloadIconButtonStyle = {
  minWidth: 40,
  width: 40,
  height: 40,
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

const PSFA_APPROVAL_ROUTE_INTERNAL = 'internal';
const PSFA_APPROVAL_ROUTE_PARENT = 'parent';
const PSFA_APPROVAL_ROUTE_REVIEW = 'psfa';
const PSFA_APPROVAL_ROUTE_REQUIRES_APPROVER = new Set([
  PSFA_APPROVAL_ROUTE_INTERNAL,
  PSFA_APPROVAL_ROUTE_PARENT,
]);
const psfaApprovalNextStepOptions = [
  {
    value: PSFA_APPROVAL_ROUTE_INTERNAL,
    label: 'Send for internal approval within my organisation (e.g. manager or peer review)',
  },
  {
    value: PSFA_APPROVAL_ROUTE_PARENT,
    label: 'Send to a parent organisation for approval or review',
  },
  {
    value: PSFA_APPROVAL_ROUTE_REVIEW,
    label: 'Submit the form to PSFA for their review',
  },
];

const waitForNextFrame = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });

type ApprovalDecisionValues = Record<string, unknown>;

type SubmitApprovalDecisionOptions = {
  rewardBadgeAcknowledged?: boolean;
  rewardApprovalLimitConfirmed?: boolean;
  rewardApprovalConfirmed?: boolean;
};

type RewardApprovalLimitWarningRow = {
  name: string;
  approvedTotal: number;
  awardAmount: number;
  projectedTotal: number;
};

type RewardTeamNomineeRecord = {
  id: string;
  source: 'original' | 'added';
  name: string;
  staffNumber: string;
  email: string;
  managerEmail: string;
  costCentre: string;
  grade: string;
  functionName: string;
};

type RewardBadgeModalState = {
  imageSrc: string;
  altText: string;
  approvalValues?: ApprovalDecisionValues;
};

const REWARD_BADGE_MODAL_BY_FORM_ID: Record<string, Omit<RewardBadgeModalState, 'approvalValues'>> = {
  '8188f10e-5ec3-458a-a3e4-8b3616ff468f': {
    imageSrc: '/images/reward-badges/slayer.png',
    altText: 'Slayer badge',
  },
  '00cd9eb5-d2ad-4244-92ab-5d85eea350cf': {
    imageSrc: '/images/reward-badges/audit.png',
    altText: 'Audit badge',
  },
};

const REWARD_BADGE_MODAL_BY_AWARD_PARAM: Record<string, Omit<RewardBadgeModalState, 'approvalValues'>> = {
  '1': REWARD_BADGE_MODAL_BY_FORM_ID['8188f10e-5ec3-458a-a3e4-8b3616ff468f'],
  '2': REWARD_BADGE_MODAL_BY_FORM_ID['00cd9eb5-d2ad-4244-92ab-5d85eea350cf'],
};

const REWARD_TEAM_EDIT_CHOICE_FIELD = 'approval-team-nominee-edit';
const REWARD_TEAM_EDIT_REASON_FIELD = 'approval-team-nominee-edit-reason';
const REWARD_TEAM_REMOVED_FIELD = 'approval-team-nominee-removed';
const REWARD_TEAM_ADDED_FIELD = 'approval-team-nominee-added';
const REWARD_ADD_NOMINEE_PREFIX = '999-';
const REWARD_ADD_NOMINEE_LOOKUP_FIELD = `${REWARD_ADD_NOMINEE_PREFIX}nominee-lookup`;
const REWARD_ADD_NOMINEE_FULL_NAME_FIELD = `${REWARD_ADD_NOMINEE_PREFIX}nominee-full-name`;
const REWARD_ADD_NOMINEE_EMAIL_FIELD = `${REWARD_ADD_NOMINEE_PREFIX}nominee-email`;
const REWARD_ADD_NOMINEE_STAFF_NUMBER_FIELD = `${REWARD_ADD_NOMINEE_PREFIX}nominee-staff-number`;
const REWARD_ADD_NOMINEE_COST_CENTRE_FIELD = `${REWARD_ADD_NOMINEE_PREFIX}nominee-cost-centre`;
const REWARD_ADD_NOMINEE_GRADE_FIELD = `${REWARD_ADD_NOMINEE_PREFIX}nominee-grade`;
const REWARD_ADD_NOMINEE_FUNCTION_FIELD = `${REWARD_ADD_NOMINEE_PREFIX}nominee-function`;
const REWARD_ADD_NOMINEE_MANAGER_EMAIL_FIELD =
  `${REWARD_ADD_NOMINEE_PREFIX}nominee-line-manager-email`;
const REWARD_ADD_NOMINEE_DATA_CORRECT_FIELD =
  `${REWARD_ADD_NOMINEE_PREFIX}nominee-data-correct`;
const REWARD_ADD_NOMINEE_INCORRECT_FIELD =
  `${REWARD_ADD_NOMINEE_PREFIX}nominee-data-incorrect-field`;
const REWARD_ADD_NOMINEE_INCORRECT_REASON_FIELD =
  `${REWARD_ADD_NOMINEE_PREFIX}nominee-data-incorrect-reason`;
const REWARD_ADD_NOMINEE_FIELD_NAMES = [
  REWARD_ADD_NOMINEE_LOOKUP_FIELD,
  REWARD_ADD_NOMINEE_FULL_NAME_FIELD,
  REWARD_ADD_NOMINEE_EMAIL_FIELD,
  REWARD_ADD_NOMINEE_STAFF_NUMBER_FIELD,
  REWARD_ADD_NOMINEE_COST_CENTRE_FIELD,
  REWARD_ADD_NOMINEE_GRADE_FIELD,
  REWARD_ADD_NOMINEE_FUNCTION_FIELD,
  REWARD_ADD_NOMINEE_MANAGER_EMAIL_FIELD,
  REWARD_ADD_NOMINEE_DATA_CORRECT_FIELD,
  REWARD_ADD_NOMINEE_INCORRECT_FIELD,
  REWARD_ADD_NOMINEE_INCORRECT_REASON_FIELD,
] as const;

const rewardTeamEditChoiceQuestion: Question = {
  questionType: 'radio',
  title: 'Would you like to change people on this nomination? (add or remove)',
  name: REWARD_TEAM_EDIT_CHOICE_FIELD,
  options: ['Yes', 'No'],
  rules: {
    required: true,
  },
  errorMessages: {
    required: 'Select yes if you would like to change people on this nomination',
  },
};

const rewardAddNomineeLookupQuestion: Question = {
  questionType: 'athenaStaffLookup',
  title: 'Search for nominee by name or email',
  name: REWARD_ADD_NOMINEE_LOOKUP_FIELD,
  hint:
    'This is the individual who you are adding to the nomination. Please enter their full name and not a nickname, otherwise the record may not be found.\n\nIt may take a few seconds to look up the staff record. Please click on the correct record when visible.',
  searchPlaceholder: 'Please enter a name or email address',
  fuzzyMatch: true,
  matchLimit: 5,
  errorMessages: {
    required: 'You must click on a user from the lookup results',
  },
  rules: {
    required: true,
  },
};

const nonSelectableReadOnlyTextareaProps = {
  readOnly: true,
  tabIndex: -1,
  'aria-readonly': true,
  onMouseDown: (event: MouseEvent<HTMLTextAreaElement>) => event.preventDefault(),
  onFocus: (event: FocusEvent<HTMLTextAreaElement>) => event.currentTarget.blur(),
  style: {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    caretColor: 'transparent',
  },
} as const;

const toExportFilenameBase = (value: string, fallback = 'answers') => {
  const normalized = String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .trim();
  return normalized || fallback;
};

const buildDownloadErrorMessage = (baseMessage: string, error: unknown): string => {
  const base = String(baseMessage || '').trim() || 'Unable to download right now.';
  const fromApiError = error instanceof ApiError ? String(error.detail || '').trim() : '';
  const fromMessage =
    !fromApiError && error && typeof error === 'object' && 'message' in error
      ? String((error as { message?: unknown }).message || '').trim()
      : '';
  const detail = fromApiError || fromMessage;
  if (!detail) return base;

  const normalizedBase = base.replace(/\.+$/, '').trim().toLowerCase();
  const normalizedDetail = detail.replace(/\.+$/, '').trim().toLowerCase();
  if (!normalizedDetail || normalizedDetail === normalizedBase) return base;
  return `${base} Cause: ${detail}`;
};

const getStringValue = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

export const isRewardSubmissionFunctionMissing = (
  tenant: string | undefined,
  answers: Record<string, unknown>,
): boolean => {
  if (tenant !== 'reward') return false;

  const nominationKind = getStringValue(answers['nomination-kind']).toLowerCase();
  if (nominationKind === 'individual') return !getStringValue(answers['nominee-function']);
  if (nominationKind === 'team') return !getStringValue(answers['responsible-function-area']);
  return false;
};

const getRewardTeamNomineeRecords = (answers: unknown): RewardTeamNomineeRecord[] => {
  if (!answers || typeof answers !== 'object') return [];

  const answerRecord = answers as Record<string, unknown>;
  const nominationKind = String(answerRecord['nomination-kind'] || '').trim().toLowerCase();
  if (nominationKind !== 'team') return [];

  return Object.entries(answerRecord)
    .map(([key, value]) => {
      const match = key.match(/^(\d+)-(?:nominee|team-member)-full-name$/);
      const name = getStringValue(value);
      if (!match || !name) return null;
      const prefix = match[1];
      const record: RewardTeamNomineeRecord = {
        id: prefix,
        source: 'original',
        name,
        staffNumber: getStringValue(answerRecord[`${prefix}-nominee-staff-number`]),
        email: getStringValue(answerRecord[`${prefix}-nominee-email`]).toLowerCase(),
        managerEmail: getStringValue(
          answerRecord[`${prefix}-nominee-line-manager-email`],
        ).toLowerCase(),
        costCentre: getStringValue(answerRecord[`${prefix}-nominee-cost-centre`]),
        grade: getStringValue(answerRecord[`${prefix}-nominee-grade`]),
        functionName: getStringValue(answerRecord[`${prefix}-nominee-function`]),
      };
      return {
        index: Number(prefix),
        record,
      };
    })
    .filter((entry): entry is { index: number; record: RewardTeamNomineeRecord } =>
      Boolean(entry),
    )
    .sort((left, right) => left.index - right.index)
    .map((entry) => entry.record);
};

const getRewardTeamNomineeNames = (answers: unknown): string[] =>
  getRewardTeamNomineeRecords(answers).map((record) => record.name);

const REWARD_APPROVAL_FIXED_VOUCHER_AMOUNTS = [
  '\u00A310',
  '\u00A325',
  '\u00A350',
  '\u00A375',
  '\u00A3100',
  '\u00A3150',
  '\u00A3200',
];

const parseCurrencyAmount = (value: unknown): number | null => {
  if (value === null || typeof value === 'undefined') return null;
  const normalized = String(value)
    .replace(/\u00A3/g, '')
    .replace(/,/g, '')
    .trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCurrencyAmount = (amount: number): string => {
  if (Number.isInteger(amount)) return String(amount);
  return amount.toFixed(2).replace(/\.?0+$/, '');
};

const roundDownToNearestFive = (amount: number): number => Math.floor(amount / 5) * 5;

const roundDownToTwoDecimalPlaces = (amount: number): number =>
  Math.floor(amount * 100) / 100;

const REWARD_ANNUAL_RECOMMENDED_AWARD_LIMIT = 2000;

const normalizeRewardApprovalType = (value: unknown): string => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized.includes('voucher')) return 'Voucher';
  if (normalized.includes('bonus') || normalized.includes('iyb')) return 'In Year Bonus';
  return '';
};

const formatRewardApprovalSelectAmount = (value: unknown): string | undefined => {
  const parsed = parseCurrencyAmount(value);
  if (parsed === null) return undefined;
  return `\u00A3${formatCurrencyAmount(parsed)}`;
};

const formatCurrencyWithCommas = (amount: number): string =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);

const getRewardApprovalDefaultValue = (
  fieldName: string,
  answers: Record<string, unknown>,
): string | undefined => {
  if (fieldName === 'approval-reward-type') {
    return normalizeRewardApprovalType(answers['reward-type']) || undefined;
  }

  if (fieldName === 'approval-iyb-amount') {
    return formatRewardApprovalSelectAmount(answers['iyb-amount']);
  }

  if (fieldName === 'approval-reward-voucher-amount') {
    const selectedValue =
      formatRewardApprovalSelectAmount(answers['reward-voucher-amount']) ||
      String(answers['reward-voucher-amount'] || '').trim();
    const otherValue = String(answers['reward-voucher-other-amount'] || '').trim();
    if (selectedValue === 'Other' || otherValue) return 'Other';
    if (REWARD_APPROVAL_FIXED_VOUCHER_AMOUNTS.includes(selectedValue)) return selectedValue;
    return parseCurrencyAmount(selectedValue) !== null ? 'Other' : undefined;
  }

  if (fieldName === 'approval-reward-voucher-other-amount') {
    const otherValue = String(answers['reward-voucher-other-amount'] || '').trim();
    if (otherValue) return String(parseCurrencyAmount(otherValue) ?? otherValue);

    const selectedValue = String(answers['reward-voucher-amount'] || '').trim();
    if (selectedValue && !REWARD_APPROVAL_FIXED_VOUCHER_AMOUNTS.includes(selectedValue)) {
      const parsed = parseCurrencyAmount(selectedValue);
      return parsed === null ? undefined : String(parsed);
    }
  }

  if (fieldName === 'approval-reward-reason') {
    const value = String(answers['nomination-justification-text'] || '').trim();
    return value || undefined;
  }

  return undefined;
};

const getRewardNomineeNames = (answers: Record<string, unknown>): string[] => {
  const nominationKind = String(answers['nomination-kind'] || '').trim().toLowerCase();
  if (nominationKind === 'team') return getRewardTeamNomineeNames(answers);

  const nomineeName = String(
    answers['individual-nominee-full-name'] || answers['nominee-full-name'] || '',
  ).trim();
  return nomineeName ? [nomineeName] : [];
};

const getRewardApprovalAwardAmount = (
  values: ApprovalDecisionValues,
  answers: Record<string, unknown>,
): number | null => {
  const awardType = String(
    values['approval-reward-type'] ||
      getRewardApprovalDefaultValue('approval-reward-type', answers) ||
      '',
  );

  if (awardType === 'In Year Bonus') {
    return parseCurrencyAmount(
      values['approval-iyb-amount'] ||
        getRewardApprovalDefaultValue('approval-iyb-amount', answers),
    );
  }

  if (awardType === 'Voucher') {
    const selectedAmount =
      values['approval-reward-voucher-amount'] ||
      getRewardApprovalDefaultValue('approval-reward-voucher-amount', answers);
    const rawAmount =
      selectedAmount === 'Other'
        ? values['approval-reward-voucher-other-amount'] ||
          getRewardApprovalDefaultValue('approval-reward-voucher-other-amount', answers)
        : selectedAmount;
    return parseCurrencyAmount(rawAmount);
  }

  return null;
};

const getRewardApprovalLimitWarningRows = (
  values: ApprovalDecisionValues,
  answers: Record<string, unknown>,
  contextTables: ContextTable[],
  nomineeNamesOverride?: string[],
): RewardApprovalLimitWarningRow[] => {
  const nomineeNames = nomineeNamesOverride || getRewardNomineeNames(answers);
  if (nomineeNames.length === 0) return [];

  const totalAwardAmount = getRewardApprovalAwardAmount(values, answers);
  if (totalAwardAmount === null) return [];

  const rewardTotalsTable = contextTables.find((table) => table.id === 'reward-totals');
  if (!rewardTotalsTable) return [];

  const nominationKind = String(answers['nomination-kind'] || '').trim().toLowerCase();
  const awardType = String(
    values['approval-reward-type'] ||
      getRewardApprovalDefaultValue('approval-reward-type', answers) ||
      '',
  );
  const perNomineeAwardAmount =
    nominationKind === 'team'
      ? awardType === 'Voucher'
        ? roundDownToNearestFive(totalAwardAmount / nomineeNames.length)
        : roundDownToTwoDecimalPlaces(totalAwardAmount / nomineeNames.length)
      : totalAwardAmount;

  const nomineeNameSet = new Set(nomineeNames.map((name) => name.trim().toLowerCase()));

  return rewardTotalsTable.rows
    .filter((row) => nomineeNameSet.has(String(row.name || '').trim().toLowerCase()))
    .map((row) => {
      const approvedTotal = parseCurrencyAmount(row.approvedTotal) ?? 0;
      const projectedTotal = approvedTotal + perNomineeAwardAmount;
      return {
        name: String(row.name || '').trim(),
        approvedTotal,
        awardAmount: perNomineeAwardAmount,
        projectedTotal,
      };
    })
    .filter((row) => row.name && row.projectedTotal > REWARD_ANNUAL_RECOMMENDED_AWARD_LIMIT);
};

type ContextTableRow = ContextTable['rows'][number];

const normalizeRewardHistoryRewardType = (value: unknown): string => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized.includes('voucher')) return 'Reward voucher';
  if (normalized.includes('bonus') || normalized.includes('iyb')) return 'In year bonus';
  return String(value || '').trim();
};

const getUniqueSortedValues = (values: string[]): string[] =>
  Array.from(new Set(values.map((value) => value.trim()).filter(Boolean))).sort((left, right) =>
    left.localeCompare(right),
  );

const isPendingRewardHistoryStatus = (value: unknown): boolean => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'awaiting panel decision' || normalized === 'info: awaiting panel decision';
};

const REWARD_HISTORY_REWARD_TYPES = ['In year bonus', 'Reward voucher'];

type HistoryDownloadIcon = 'excel' | 'pdf' | 'html';

type RewardHistoryQuickViewState = {
  formId: string;
  html: string;
  error: string;
  isComparisonView: boolean;
};

// Font Awesome Free file icons, used inline to avoid adding a new icon package.
const historyDownloadIconPaths: Record<HistoryDownloadIcon, string> = {
  excel:
    'M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V160H256c-17.7 0-32-14.3-32-32V0H64zM256 0V128H384L256 0zM155.7 250.2L192 302.1l36.3-51.9c7.6-10.9 22.6-13.5 33.4-5.9s13.5 22.6 5.9 33.4L221.3 344l46.4 66.2c7.6 10.9 5 25.8-5.9 33.4s-25.8 5-33.4-5.9L192 385.8l-36.3 51.9c-7.6 10.9-22.6 13.5-33.4 5.9s-13.5-22.6-5.9-33.4L162.7 344l-46.4-66.2c-7.6-10.9-5-25.8 5.9-33.4s25.8-5 33.4 5.9z',
  pdf:
    'M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V160H256c-17.7 0-32-14.3-32-32V0H64zM256 0V128H384L256 0zM64 224H96c35.3 0 64 28.7 64 64s-28.7 64-64 64H88v32c0 13.3-10.7 24-24 24s-24-10.7-24-24V248c0-13.3 10.7-24 24-24zm32 80c8.8 0 16-7.2 16-16s-7.2-16-16-16H88v32h8zm72-56c0-13.3 10.7-24 24-24h32c26.5 0 48 21.5 48 48v88c0 26.5-21.5 48-48 48H192c-13.3 0-24-10.7-24-24V248zm48 112h8V272h-8v88zm96-136h48c13.3 0 24 10.7 24 24s-10.7 24-24 24H336v32h24c13.3 0 24 10.7 24 24s-10.7 24-24 24H336v32c0 13.3-10.7 24-24 24s-24-10.7-24-24V248c0-13.3 10.7-24 24-24z',
  html:
    'M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V160H256c-17.7 0-32-14.3-32-32V0H64zM256 0V128H384L256 0zM152.4 246.6c9.4 9.4 9.4 24.6 0 33.9L116.9 316l35.5 35.5c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L66 332.9c-9.4-9.4-9.4-24.6 0-33.9l52.5-52.5c9.4-9.4 24.6-9.4 33.9 .1zm79.2 0c9.4-9.4 24.6-9.4 33.9-.1L318 299c9.4 9.4 9.4 24.6 0 33.9l-52.5 52.5c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l35.5-35.5-35.5-35.5c-9.4-9.4-9.4-24.6 0-33.9z',
};

const DownloadFileIcon = ({ icon }: { icon: HistoryDownloadIcon }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 384 512"
    width="20"
    height="24"
    fill="currentColor"
    focusable="false"
  >
    <path d={historyDownloadIconPaths[icon]} />
  </svg>
);

const QuickViewIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 512 512"
    width="22"
    height="22"
    fill="currentColor"
    focusable="false"
  >
    <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376C296.3 401.1 253.9 416 208 416C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z" />
  </svg>
);

const toOptionalApprovalQuestion = (question: Question): Question => {
  const options = Array.isArray(question.options)
    ? question.options.map((option: SelectOption) => {
        if (typeof option === 'string') return option;
        if ('option' in option) {
          return {
            ...option,
            questions: option.questions?.map((nested) => toOptionalApprovalQuestion(nested)),
          };
        }
        return option;
      })
    : question.options;

  return {
    ...question,
    rules: {
      ...(question.rules || {}),
      required: false,
    },
    options,
  };
};

const getFormStatusLabel = (
  formState: string | undefined,
  hasApprover: boolean,
  hasAdminApprovalTarget: boolean,
  isAdmin: boolean,
  strictDeadline: boolean,
  approvalStatus?: string,
  approvalRound?: number,
  pendingApprovalQuarter?: string,
  formCloseAt?: string,
  statusOverride?: FormConfig['statusOverride'],
) => {
  const resolvedApprovalStatus = String(approvalStatus || '').trim();
  const resolvedPendingApprovalQuarter = String(pendingApprovalQuarter || '').trim().toUpperCase();
  if (formState === 'approval') {
    if (typeof approvalRound === 'number' && approvalRound > 1) {
      return `${getOrdinal(approvalRound)} Approval`;
    }
    return /^Q[1-4]$/.test(resolvedPendingApprovalQuarter)
      ? `${resolvedPendingApprovalQuarter} Approval`
      : 'Approval';
  }
  if (formState === 'review') return isAdmin ? 'Pending Review' : 'Approved';
  if (formState === 'rejected') return 'Rejected';
  if (formState === 'expired') return 'Expired';
  if (formState === 'submitted') {
    const submittedOverride = statusOverride?.submitted?.trim();
    return submittedOverride || (hasApprover || hasAdminApprovalTarget ? 'Approved' : 'Submitted');
  }
  if (formState === 'approved') return resolvedApprovalStatus || 'Approved';
  if (formState === 'exported') return 'Exported';
  if (strictDeadline && formCloseAt && isPastLondon(formCloseAt)) return 'Expired';
  if (formState === 'active' && resolvedApprovalStatus) return resolvedApprovalStatus;
  return 'In progress';
};

const getStatusTagClass = (label: string) => {
  const lower = label.toLowerCase();
  if (lower.startsWith('submitted')) return 'govuk-tag--green';
  if (lower.startsWith('approved')) return 'govuk-tag--green';
  if (lower.startsWith('in progress')) return 'govuk-tag--blue';
  if (lower.startsWith('approval')) return 'govuk-tag--blue';
  if (lower.startsWith('pending approval') || lower.startsWith('pending review')) return 'govuk-tag--blue';
  if (/^\d+(st|nd|rd|th)\s+approval$/.test(lower)) return 'govuk-tag--blue';
  if (/^q[1-4]\s+approval$/.test(lower)) return 'govuk-tag--green';
  if (/^q[1-4]\s+complete$/.test(lower)) return 'govuk-tag--green';
  if (lower.startsWith('exported')) return 'govuk-tag--grey';
  if (lower.startsWith('expired')) return 'govuk-tag--red';
  if (lower.startsWith('rejected')) return 'govuk-tag--red';
  return 'govuk-tag--grey';
};

const parseReportPeriodValue = (value: unknown): { quarter: string; year: string } => {
  if (!value) return { quarter: '', year: '' };
  if (typeof value === 'object') {
    const parsed = value as { quarter?: unknown; year?: unknown };
    return {
      quarter: typeof parsed.quarter === 'string' ? parsed.quarter : '',
      year: typeof parsed.year === 'string' ? parsed.year : '',
    };
  }
  if (typeof value !== 'string') return { quarter: '', year: '' };
  try {
    return parseReportPeriodValue(JSON.parse(value));
  } catch (_err) {
    return { quarter: '', year: value.trim() };
  }
};

type ApprovalHistoryEntry = {
  approvalKey: string;
  quarter: string;
  year: string;
  status: string;
  comments: string;
  approvedAt: string;
};

type ApprovalRequestHistoryEntry = {
  key: string;
  round: number;
  requesterEmail: string;
  approverEmail: string;
  notes: string;
  requestedAt: string;
};

const getOrdinal = (value: number) => {
  const roundedValue = Math.trunc(value);
  const suffix =
    roundedValue % 100 >= 11 && roundedValue % 100 <= 13
      ? 'th'
      : roundedValue % 10 === 1
        ? 'st'
        : roundedValue % 10 === 2
          ? 'nd'
          : roundedValue % 10 === 3
            ? 'rd'
            : 'th';
  return `${roundedValue}${suffix}`;
};

const parseApprovalRequestHistoryEntries = (
  rawHistory: unknown,
): ApprovalRequestHistoryEntry[] => {
  if (!Array.isArray(rawHistory)) return [];

  return rawHistory
    .map((entry, index) => {
      const record = entry as ApprovalRequestRecord;
      const round = Number(record?.round || index + 1);
      const requestedAt = String(record?.requested_at || record?.requestedAt || '').trim();
      return {
        key: `${Number.isFinite(round) && round > 0 ? round : index + 1}-${requestedAt || index}`,
        round: Number.isFinite(round) && round > 0 ? round : index + 1,
        requesterEmail: String(record?.requester_email || record?.requesterEmail || '').trim(),
        approverEmail: String(record?.approver_email || record?.approverEmail || '').trim(),
        notes: String(record?.notes || '').trim(),
        requestedAt,
      };
    })
    .sort((left, right) => left.round - right.round);
};

const quarterSortOrder: Record<string, number> = {
  Q1: 1,
  Q2: 2,
  Q3: 3,
  Q4: 4,
};

const parseApprovalHistoryEntries = (
  rawApprovals: unknown,
): ApprovalHistoryEntry[] => {
  if (!rawApprovals || typeof rawApprovals !== 'object') return [];

  return Object.entries(rawApprovals as Record<string, ApprovalRecord>)
    .map(([approvalKey, approval]) => {
      const quarter = String(approval?.quarter || '').trim().toUpperCase();
      const year = String(approval?.year || '').trim();
      const status = String(approval?.status || '').trim() || approvalKey;
      const comments = String(approval?.comments || '').trim();
      const approvedAt = String(approval?.approved_at || approval?.approvedAt || '').trim();

      return {
        approvalKey,
        quarter,
        year,
        status,
        comments,
        approvedAt,
      };
    })
    .filter((entry) => entry.approvalKey)
    .sort((left, right) => {
      if (left.year !== right.year) return left.year.localeCompare(right.year);
      const quarterDelta =
        (quarterSortOrder[left.quarter] || Number.MAX_SAFE_INTEGER) -
        (quarterSortOrder[right.quarter] || Number.MAX_SAFE_INTEGER);
      if (quarterDelta !== 0) return quarterDelta;
      return left.approvedAt.localeCompare(right.approvedAt);
    });
};

const PERFORMANCE_SUBSECTION_BY_QUARTER: Record<'Q1' | 'Q2' | 'Q3' | 'Q4', string> = {
  Q1: 'performance-q1',
  Q2: 'performance-q2',
  Q3: 'performance-q3',
  Q4: 'performance-q4',
};

const resolveLatestCompletedQuarter = (
  subsectionStatuses: Record<string, Status>,
): 'Q1' | 'Q2' | 'Q3' | 'Q4' | '' => {
  const ordered = ['Q4', 'Q3', 'Q2', 'Q1'] as const;
  for (const quarter of ordered) {
    const status = subsectionStatuses[PERFORMANCE_SUBSECTION_BY_QUARTER[quarter]];
    if ([Status.Completed, Status.NilReturn].includes(status ?? Status.NotStarted)) {
      return quarter;
    }
  }
  return '';
};

const defaultApprovalDecisionHint =
  'If you reject this form, the author will have the option to update and resubmit it.';
const scsQuarterApprovalDecisionHint =
  'If you reject this form, the author will have the option to update and resubmit it.<br />This form, once approved, will be shared with the central Performance Management Team';
const defaultApprovalCommentHint = 'This will be shared with the form author';
const defaultApprovalRejectionCommentHint =
  'This will be shared with the form author when they review it for re-submission';

const safeReturnPath = (value: unknown, tenant: string) => {
  const raw = String(value || '').trim();
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '';
  const tenantPrefix = `/${tenant}`;
  return raw === tenantPrefix || raw.startsWith(`${tenantPrefix}?`) ? raw : '';
};

export const shouldLimitSummaryWidth = (
  subsections: Array<{ pagesForSummary: Page[] }>,
): boolean =>
  !subsections.some(({ pagesForSummary }) =>
    pagesForSummary.some(
      (page) => page.pageType !== 'information' && page.limitWidth === false,
    ),
  );

export const FormSummary = () => {
  const { isLoading } = useUiContext();
  const { formConfig, enabledSubsections, tenant } = useFormConfig();
  const navigate = useNavigate();
  const { sections } = formConfig;
  const location = useLocation();
  const currentSearchParams = new URLSearchParams(location.search || '');
  const isTestSubmissionMode =
    parseOptionalBoolValue(currentSearchParams.get('test')) === true;
  const isIncompleteCheckMode =
    parseOptionalBoolValue(currentSearchParams.get('check_answers')) === true;
  const queryClient = useQueryClient();
  const { fetchJWT } = useAuthContext();
  const selectedFormId = resolveSelectedFormId(tenant, location?.search || '');
  const tenantPath = tenant || '';
  const myFormsPath = `/${tenantPath}?my_forms=1&admin=0`;
  const stateReturnTo = safeReturnPath((location.state as any)?.returnTo, tenantPath);
  const returnToFormsPath =
    stateReturnTo ||
    (new URLSearchParams(location.search || '').get('admin') === '1'
      ? `/${tenantPath}?my_forms=1&admin=1`
      : myFormsPath);
  const returnToFormsLabel =
    new URLSearchParams(returnToFormsPath.split('?')[1] || '').get('admin') === '1'
      ? 'Go to Admin View'
      : 'Go to My Forms';

  const [openSections, setOpenSections] = useState<string[]>([]);
  const approvalId = useId();

  // fetch personal data
  const { data } = useFormData();
  const approvalMethods = useForm<Record<string, unknown>>({
    defaultValues: data?.answers || {},
  });
  const [approvalNotes, setApprovalNotes] = useState('');
  const [selectedApprover, setSelectedApprover] = useState('');
  const [otherApproverEmail, setOtherApproverEmail] = useState('');
  const [approverSelectionError, setApproverSelectionError] = useState<string | null>(null);
  const [approverEmailError, setApproverEmailError] = useState<string | null>(null);
  const [approvalRequestChoice, setApprovalRequestChoice] = useState('');
  const [approverForwardChoice, setApproverForwardChoice] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');
  const [submittedApproverEmail, setSubmittedApproverEmail] = useState('');
  const [approvalSubmitError, setApprovalSubmitError] = useState<string | null>(null);
  const [approvalRedirectCountdown, setApprovalRedirectCountdown] = useState<number | null>(null);
  const [pendingRewardBadgeModal, setPendingRewardBadgeModal] =
    useState<RewardBadgeModalState | null>(() => {
      const badge = REWARD_BADGE_MODAL_BY_AWARD_PARAM[currentSearchParams.get('award') || ''];
      return badge ? { ...badge } : null;
    });
  const [pendingRewardApprovalValues, setPendingRewardApprovalValues] =
    useState<ApprovalDecisionValues | null>(null);
  const [pendingRewardApprovalLimitWarningValues, setPendingRewardApprovalLimitWarningValues] =
    useState<ApprovalDecisionValues | null>(null);
  const [rewardApprovalLimitWarningRows, setRewardApprovalLimitWarningRows] = useState<
    RewardApprovalLimitWarningRow[]
  >([]);
  const [hasConfirmedRewardApproval, setHasConfirmedRewardApproval] = useState(false);
  const [isRewardApprovalConfirmSubmitting, setIsRewardApprovalConfirmSubmitting] = useState(false);
  const [historyDownloadError, setHistoryDownloadError] = useState('');
  const [activeHistoryDownload, setActiveHistoryDownload] = useState('');
  const [rewardHistoryQuickView, setRewardHistoryQuickView] =
    useState<RewardHistoryQuickViewState | null>(null);
  const rewardHistoryQuickViewCloseRef = useRef<HTMLButtonElement>(null);
  const [selectedRewardHistoryRewardType, setSelectedRewardHistoryRewardType] = useState('');
  const [selectedRewardHistoryFunction, setSelectedRewardHistoryFunction] = useState('');
  const [showRewardHistoryPendingOnly, setShowRewardHistoryPendingOnly] = useState(false);
  const [rewardRemovedTeamNomineeIds, setRewardRemovedTeamNomineeIds] = useState<string[]>([]);
  const [rewardAddedTeamNominees, setRewardAddedTeamNominees] = useState<
    RewardTeamNomineeRecord[]
  >([]);
  const [rewardRemovedAddedTeamNomineeIds, setRewardRemovedAddedTeamNomineeIds] = useState<
    string[]
  >([]);
  const [isRewardAddNomineeModalOpen, setIsRewardAddNomineeModalOpen] = useState(false);
  const [rewardAddNomineeError, setRewardAddNomineeError] = useState('');

  useEffect(() => {
    if (rewardHistoryQuickView?.formId) rewardHistoryQuickViewCloseRef.current?.focus();
  }, [rewardHistoryQuickView?.formId]);

  const resolveMutationErrorMessage = (error: unknown): string => {
    if (error instanceof ApiError) {
      return error.detail || error.message || content.errorApi;
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return content.errorApi;
  };

  const formCloseAt = (formConfig as any)?.formCloseAt ?? (data as any)?.formCloseAt;
  const selectedOrLoadedFormId =
    selectedFormId || String((data as any)?.formId || '').trim() || undefined;
  const rewardBadgeModalForCurrentForm = selectedOrLoadedFormId
    ? REWARD_BADGE_MODAL_BY_FORM_ID[selectedOrLoadedFormId]
    : undefined;
  const indexPath = tenant ? `/${tenant}` : '/';
  const indexPathWithFormId = selectedOrLoadedFormId
    ? `${indexPath}?formId=${selectedOrLoadedFormId}`
    : indexPath;
  const strictDeadline =
    (formConfig as any)?.strictDeadline ?? (data as any)?.strictDeadline ?? false;
  const isSubmitted = Boolean(data?.submitted);
  const formState = (data as any)?.formState as string | undefined;
  const isDownloadableSummaryState =
    isSubmitted || formState === 'approved' || formState === 'review';
  const hasApprover = Boolean((data as any)?.hasApprover);
  const role = String(data?.role || '').toLowerCase();
  const isEditableRole = role === 'owner' || role === 'editor';
  const isApprover = role === 'approver';
  const isAdmin = role === 'admin';
  const searchParams = new URLSearchParams(location.search);
  const reviewParam = searchParams.get('review');
  const isReviewRequested = reviewParam === '1';
  const isAssignedApprover = (data as any)?.isAssignedApprover === true;
  const isApprovalActor = isApprover || isAssignedApprover;
  const isApproval = formState === 'approval';
  const isReview = formState === 'review';
  const isRewardInfoOnly = tenant === 'reward' && (data as any)?.rewardInfoOnly === true;
  const approvalFixedEmail =
    typeof formConfig.approvalFixedEmail === 'string' &&
    formConfig.approvalFixedEmail.trim().length > 0
      ? formConfig.approvalFixedEmail.trim().toLowerCase()
      : '';
  const hasAdminApprovalTarget = approvalFixedEmail === 'admin';
  const isRewardAdminApprovalRequest =
    tenant === 'reward' &&
    parseOptionalBoolValue(searchParams.get('admin')) === true &&
    isAdmin &&
    isApproval &&
    hasAdminApprovalTarget &&
    !isRewardInfoOnly;
  const isPreviewMode = isAdminPreviewMode({
    search: location?.search || '',
    selectedFormId: selectedOrLoadedFormId,
    hasAdminAccess: data?.hasAdminAccess,
    role: data?.role,
    sessionRoles: data?.sessionRoles,
    forms: data?.forms,
    allowPendingAccessCheck: true,
  }) && !isReviewRequested && !isRewardAdminApprovalRequest;
  const previewSearchSuffix = isPreviewMode ? '?admin=1' : '';
  const isReadOnlyRole = role === 'read_only' || (role === 'admin' && !isPreviewMode);
  const isApproverReadOnly = isApprovalActor && formState === 'active';
  const isEditableApprovalBeforeClose = canEditApprovalBeforeClose({
    approvalCloseOnQuarter: formConfig.approvalCloseOnQuarter,
    formState,
    role,
    pendingApprovalQuarter: (data as any)?.pendingApprovalQuarter,
  });
  const canActAsApprovalApprover =
    !isApprovalActor || !isApproval || (data as any)?.canViewApprovalAnswers !== false;
  const reviewMode =
    isRewardAdminApprovalRequest ||
    (!isPreviewMode &&
      isReviewRequested &&
      !isRewardInfoOnly &&
      ((isApprovalActor && isApproval && canActAsApprovalApprover) ||
        (isAdmin && (isReview || (isApproval && hasAdminApprovalTarget)))));
  const isNonAdminReviewReadOnly = isReview && !reviewMode && !isAdmin;
  const isReadOnlyViewMode =
    (isReadOnlyRole && !reviewMode) || isApproverReadOnly || isNonAdminReviewReadOnly;
  const isRejected = formState === 'rejected';
  const allowApprovalUnsubmit = formConfig.approvalAllowUnsubmit === true;
  const showWithdrawApprovalControls =
    allowApprovalUnsubmit && isApproval && isEditableRole && !isPreviewMode;
  const approvalMode = formConfig.approval;
  const reviewEnabled = formConfig.review === true;
  const configuredApprovalDecisionHint =
    typeof formConfig.approvalDecisionHint === 'string' &&
    formConfig.approvalDecisionHint.trim().length > 0
      ? formConfig.approvalDecisionHint
      : defaultApprovalDecisionHint;
  const approvalRequestHint =
    typeof formConfig.approvalRequestHint === 'string' &&
    formConfig.approvalRequestHint.trim().length > 0
      ? formConfig.approvalRequestHint
      : '';
  const configuredApprovalCommentHint =
    typeof formConfig.approvalCommentHint === 'string' &&
    formConfig.approvalCommentHint.trim().length > 0
      ? formConfig.approvalCommentHint
      : '';
  const approvalDecisionLabel =
    tenant === 'reward'
      ? 'Does the panel approve this nomination?'
      : 'Do you approve this form for submission?';
  const requiresApproval = approvalMode === 'always';
  const isOptionalApproval = approvalMode === 'optional';
  // A form in 'rejected'/'approval'/'review' state implies the approval flow is in play for this form,
  // even if the current tenant config is not explicitly set to require/enable approvals.
  const approvalFlowEnabled =
    requiresApproval ||
    isOptionalApproval ||
    reviewEnabled ||
    hasApprover ||
    hasAdminApprovalTarget ||
    isRejected ||
    isApproval ||
    isReview;
  const isExpired =
    !isSubmitted &&
    (Boolean(data?.expired) || (strictDeadline && isPastLondon(formCloseAt ?? undefined)));
  const submittedAt = (data as any)?.submittedAt ?? (data as any)?.submitted_at;
  const expiredAt = (data as any)?.expiredAt ?? (data as any)?.expired_at ?? formCloseAt;

  const submittedText = formatLondonDateTime(submittedAt || undefined);
  const expiredText = formatLondonDateTime(expiredAt || undefined);

  useEffect(() => {
    if (approvalRedirectCountdown === null) return undefined;

    const timerId = window.setTimeout(() => {
      if (approvalRedirectCountdown <= 0) {
        setSelectedFormIdInSession(tenant, null);
        navigate(myFormsPath);
        return;
      }

      setApprovalRedirectCountdown((currentValue) =>
        currentValue === null ? null : currentValue - 1,
      );
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [approvalRedirectCountdown, myFormsPath, navigate, tenant]);

  const approvalRequestedText =
    typeof (data as any)?.approvalRequestedAt === 'string'
      ? String((data as any).approvalRequestedAt).trim()
      : '';
  const approvalRequesterEmail =
    typeof (data as any)?.approvalRequesterEmail === 'string'
      ? String((data as any).approvalRequesterEmail).trim()
      : '';
  const approvalRequestNote =
    typeof (data as any)?.approvalRequestNote === 'string'
      ? String((data as any).approvalRequestNote).trim()
      : '';
  const approvalApproverEmail =
    typeof (data as any)?.approvalApproverEmail === 'string'
      ? String((data as any).approvalApproverEmail).trim()
      : '';
  const approvalWithdrawnNote =
    typeof (data as any)?.approvalWithdrawnNote === 'string'
      ? String((data as any).approvalWithdrawnNote).trim()
      : '';
  const approvalWithdrawnByEmail =
    typeof (data as any)?.approvalWithdrawnByEmail === 'string'
      ? String((data as any).approvalWithdrawnByEmail).trim()
      : '';
  const rewardTransferOriginalFunction =
    typeof data?.rewardTransferOriginalFunction === 'string'
      ? data.rewardTransferOriginalFunction.trim()
      : '';
  const isRewardFunctionPanelReview =
    tenant === 'reward' &&
    isAdmin &&
    isReviewRequested &&
    reviewMode &&
    data?.rewardHasFunctionApproverRole === true;
  const showRewardTransferBanner =
    isRewardFunctionPanelReview &&
    Boolean(rewardTransferOriginalFunction);

  const showRewardSubmittedUserBanner =
    tenant === 'reward' &&
    isSubmitted &&
    isEditableRole &&
    !isAdmin &&
    !isPreviewMode &&
    !reviewMode;
  const bannerHeader = showRewardSubmittedUserBanner
    ? 'Success'
    : isSubmitted
      ? 'Viewing submitted answers'
      : 'Viewing Unsubmitted answers';
  const bannerLines = showRewardSubmittedUserBanner
    ? ['Thank you. Your reward nomination has been submitted.']
    : isSubmitted
      ? ([submittedText ? `Submitted on ${submittedText}` : null].filter(Boolean) as string[])
      : isExpired
        ? ([expiredText ? `Expired on ${expiredText}` : null].filter(Boolean) as string[])
        : [];
  const bannerContent = bannerLines.length ? bannerLines.join('<br />') : ' ';
  const showBanner = (isSubmitted || isExpired) && bannerLines.length > 0;
  const rejectionNote =
    typeof (data as any)?.rejectionNote === 'string'
      ? String((data as any).rejectionNote).trim()
      : '';
  const isViewOnly =
    isExpired ||
    isSubmitted ||
    formState === 'approved' ||
    formState === 'exported' ||
    (isApproval && !reviewMode && !isEditableApprovalBeforeClose) ||
    (isReview && !reviewMode) ||
    (isRejected && !isEditableRole) ||
    (isApprovalActor && formState === 'active') ||
    (isReadOnlyRole && !reviewMode);
  const disableSummaryChangeLinks =
    isViewOnly || (isApprovalActor && reviewMode) || isRewardAdminApprovalRequest;
  const isSubmissionDisabledInPreview = isPreviewMode;

  const progressData = useMemo(
    () => getOverviewStatus(sections, enabledSubsections, data),
    [sections, enabledSubsections, data],
  );
  const hasIncompleteSubsections = hasIncompleteRequiredSubsections(sections, progressData);
  const isIncompleteRewardSubmission =
    isEditableRole &&
    !isPreviewMode &&
    ['active', 'rejected', 'updating'].includes(formState || '') &&
    isRewardSubmissionFunctionMissing(
      tenant,
      ((data as any)?.answers || {}) as Record<string, unknown>,
    );
  const isIncompleteSummaryView =
    (hasIncompleteSubsections || isIncompleteRewardSubmission) && !isTestSubmissionMode;
  const approvalQuarter = useMemo(
    () => (data ? resolveLatestCompletedQuarter(progressData as Record<string, Status>) : ''),
    [data, progressData],
  );
  const approvalPageEntry = useMemo(() => {
    const isReviewPageTarget = isAdmin && isReview && !formConfig.approveFormCondition;
    for (const section of sections) {
      for (const subsection of section.subsections) {
        for (const page of subsection.pages) {
          const isTargetPage = isReviewPageTarget
            ? page.name === 'review'
            : isApprovalPageName(page.name);
          if (
            isTargetPage &&
            'questions' in page &&
            !isPageDisabled(page, (data as any) || { answers: {} })
          ) {
            return { page, subsection, section };
          }
        }
      }
    }
    return null;
  }, [data, formConfig.approveFormCondition, isAdmin, isReview, sections]);
  const activeApprovalPageName = approvalPageEntry?.page?.name || '';
  const watchedApprovalDecision = String(approvalMethods.watch('approvalDecision') || '').toLowerCase();
  const approvalDecisionHint =
    tenant === 'scs' && ['approval-q1', 'approval-q2', 'approval-q3'].includes(activeApprovalPageName)
      ? scsQuarterApprovalDecisionHint
      : configuredApprovalDecisionHint;
  const approvalCommentHint =
    configuredApprovalCommentHint ||
    (watchedApprovalDecision === 'no'
      ? defaultApprovalRejectionCommentHint
      : defaultApprovalCommentHint);

  const approvalQuestions = (approvalPageEntry?.page as any)?.questions || [];
  const approveFormCondition = formConfig.approveFormCondition;
  const showApprovalFormQuestions =
    approveFormCondition === 'approve'
      ? watchedApprovalDecision === 'yes'
      : approveFormCondition === 'reject'
        ? watchedApprovalDecision === 'no'
        : true;
  const visibleApprovalQuestions = showApprovalFormQuestions ? approvalQuestions : [];
  const rewardFormLocation = tenant === 'reward' ? formConfig.rewardFormLocation || 'top' : 'top';
  const showApprovalQuestionsAtTop = rewardFormLocation !== 'bottom';
  const showApprovalQuestionsAtBottom = rewardFormLocation === 'bottom';
  const approvalQuestionNames = useMemo(
    () => extractConditionalQuestions(visibleApprovalQuestions as Question[]).map((q) => q.name),
    [visibleApprovalQuestions],
  );
  const activeApprovalReportPeriodQuestionName = useMemo(
    () =>
      approvalQuestionNames.find((name) => name.startsWith('approval-report-period')) ||
      'approval-report-period',
    [approvalQuestionNames],
  );
  const approvalYear = useMemo(() => {
    const approvalReportPeriod = parseReportPeriodValue(
      data?.answers?.[activeApprovalReportPeriodQuestionName],
    );
    if (approvalReportPeriod.year) return approvalReportPeriod.year;
    const coverSheetReportPeriod = parseReportPeriodValue(
      data?.answers?.['report-period'] ?? data?.answers?.['person-details-report-period'],
    );
    if (coverSheetReportPeriod.year) return coverSheetReportPeriod.year;
    const storedReportPeriod = parseReportPeriodValue((data as any)?.reportPeriod);
    return storedReportPeriod.year;
  }, [activeApprovalReportPeriodQuestionName, data]);
  const approvalHistoryEntries = useMemo(
    () => parseApprovalHistoryEntries((data as any)?.approvals),
    [data],
  );
  const approvalRequestHistoryEntries = useMemo(
    () => parseApprovalRequestHistoryEntries((data as any)?.approvalRequestHistory),
    [data],
  );
  const visibleSummarySubsections = useMemo(
    () =>
      sections.flatMap((section) => {
        return section.subsections
          .map((subsection) => {
            if (isSubsectionNotRequired(subsection, data)) return null;
            const pagesForSummaryBase = subsection.dynamicPages
              ? enabledSubsections[subsection.slug]?.pages || []
              : subsection.pages;
            const pagesForSummary = pagesForSummaryBase.filter(
              (page) => !isWorkflowOnlyPageName(page.name),
            );
            if (pagesForSummary.length === 0) return null;
            if (progressData[subsection.slug] === Status.NotDue) return null;
            return { section, subsection, pagesForSummary };
          })
          .filter(Boolean);
      }) as Array<{
        section: Section;
        subsection: Section['subsections'][number];
        pagesForSummary: typeof sections[number]['subsections'][number]['pages'];
      }>,
    [data, enabledSubsections, progressData, sections],
  );
  const visibleSummarySubsectionSlugs = useMemo(
    () => visibleSummarySubsections.map(({ subsection }) => subsection.slug),
    [visibleSummarySubsections],
  );
  const limitSummaryWidth = useMemo(
    () => shouldLimitSummaryWidth(visibleSummarySubsections),
    [visibleSummarySubsections],
  );
  const showApprovalScrollLink = reviewMode;
  const showRejectionNoteBanner = isRejected;
  const showApprovalWithdrawnNoteBanner = isApproverReadOnly && Boolean(approvalWithdrawnNote);
  const showSubmitterNoteBanner =
    reviewMode && (!isRewardFunctionPanelReview || Boolean(approvalRequestNote));
  const hasApprovalRequestDetails = Boolean(approvalRequestedText || approvalRequestNote);
  const hasAssignedApprovalHandler = hasApprover || hasAdminApprovalTarget;
  const showApprovalRequestSummary =
    hasAssignedApprovalHandler &&
    (hasApprovalRequestDetails ||
      isApproval ||
      isReview ||
      isRejected ||
      formState === 'submitted' ||
      formState === 'exported');
  const showSavedRewardApprovalAnswers =
    tenant === 'reward' &&
    !reviewMode &&
    ['submitted', 'approved', 'exported'].includes(formState || '') &&
    data?.canViewApprovalAnswers === true &&
    extractConditionalQuestions(approvalQuestions).some(({ name }) =>
      hasValue(data?.answers?.[name]),
    );
  const showApprovalsSection =
    showApprovalRequestSummary ||
    showSavedRewardApprovalAnswers ||
    approvalRequestHistoryEntries.length > 0 ||
    approvalHistoryEntries.length > 0;
  const showRewardReviewApprovalNotesOnly = tenant === 'reward' && reviewMode;
  const approvalTitleBase =
    typeof formConfig.formName === 'string' && formConfig.formName.trim()
      ? formConfig.formName.trim()
      : (tenant || 'FORM').toUpperCase();
  const readOnlyModeLabel = isApproverReadOnly
    ? `${approvalTitleBase}: Pre-approval`
    : `${approvalTitleBase}: Read only`;
  const formStatusLabel = getFormStatusLabel(
    formState,
    hasApprover,
    hasAdminApprovalTarget,
    isAdmin,
    strictDeadline,
    (data as any)?.approvalStatus,
    Number((data as any)?.approvalRound || 0),
    approvalQuarter,
    formCloseAt,
    formConfig.statusOverride,
  );
  const contextTables = Array.isArray((data as any)?.contextTables)
    ? ((data as any).contextTables as ContextTable[])
    : [];
  const rewardHistoryFilterOptions = useMemo(() => {
    const rewardHistoryTable = contextTables.find((table) => table.id === 'reward-history');
    const rows = rewardHistoryTable?.rows || [];
    return {
      rewardTypes: REWARD_HISTORY_REWARD_TYPES,
      functions: getUniqueSortedValues(rows.map((row) => String(row.function || ''))),
    };
  }, [contextTables]);
  const showRewardHistoryFilters =
    tenant === 'reward' &&
    !isRewardAdminApprovalRequest &&
    contextTables.some((table) => table.id === 'reward-history');
  const filteredContextTables = useMemo(() => {
    if (!showRewardHistoryFilters) return contextTables;

    return contextTables.map((table) => {
      if (table.id !== 'reward-history') return table;

      const filteredRows = table.rows.filter((row: ContextTableRow) => {
        const rewardType = normalizeRewardHistoryRewardType(row.rewardType);
        if (selectedRewardHistoryRewardType && rewardType !== selectedRewardHistoryRewardType) {
          return false;
        }
        if (
          selectedRewardHistoryFunction &&
          String(row.function || '').trim() !== selectedRewardHistoryFunction
        ) {
          return false;
        }
        if (showRewardHistoryPendingOnly && !isPendingRewardHistoryStatus(row.status)) {
          return false;
        }
        return true;
      });

      return {
        ...table,
        rows: filteredRows,
      };
    });
  }, [
    contextTables,
    selectedRewardHistoryFunction,
    selectedRewardHistoryRewardType,
    showRewardHistoryFilters,
    showRewardHistoryPendingOnly,
  ]);
  const rewardNominationIdentifier = String((data as any)?.identifier || '').trim();
  const rewardOriginalTeamNominees = useMemo(
    () => getRewardTeamNomineeRecords((data as any)?.answers),
    [data?.answers],
  );
  const rewardTeamNomineeNames = rewardOriginalTeamNominees.map((record) => record.name);
  const watchedRewardChangeNomination = String(
    approvalMethods.watch('approval-change-nomination') || '',
  );
  const watchedRewardTeamEditChoice = String(
    approvalMethods.watch(REWARD_TEAM_EDIT_CHOICE_FIELD) || '',
  );
  const showRewardTeamPeopleEdit =
    tenant === 'reward' &&
    activeApprovalPageName === 'approval' &&
    String((data as any)?.answers?.['nomination-kind'] || '').trim().toLowerCase() === 'team' &&
    watchedRewardChangeNomination === 'Yes';
  const shouldApplyRewardTeamPeopleEdits =
    showRewardTeamPeopleEdit && watchedRewardTeamEditChoice === 'Yes';
  const activeRewardAddedTeamNominees = rewardAddedTeamNominees.filter(
    (record) => !rewardRemovedAddedTeamNomineeIds.includes(record.id),
  );
  const effectiveRewardTeamNominees = shouldApplyRewardTeamPeopleEdits
    ? [
        ...rewardOriginalTeamNominees.filter(
          (record) => !rewardRemovedTeamNomineeIds.includes(record.id),
        ),
        ...activeRewardAddedTeamNominees,
      ]
    : rewardOriginalTeamNominees;
  const effectiveRewardTeamNomineeNames = effectiveRewardTeamNominees.map((record) => record.name);
  const showRewardNominationIdentifier =
    tenant === 'reward' &&
    rewardNominationIdentifier.length > 0 &&
    contextTables.some((table) => table?.id === 'reward-history');
  const pageTitle = showBanner
    ? isReadOnlyViewMode
      ? approvalTitleBase
      : undefined
    : isReadOnlyViewMode
      ? approvalTitleBase
      : isExpired
        ? 'Viewing Unsubmitted answers'
        : showWithdrawApprovalControls
          ? 'Form is awaiting approval'
          : reviewMode
            ? isRewardAdminApprovalRequest
              ? `${approvalTitleBase} Function Panel Approval`
              : isReview
                ? `${approvalTitleBase} Review`
                : `${approvalTitleBase} Approval`
            : isRejected
              ? 'Revise your answers'
              : 'Check your answers';
  const breadcrumbEndLabel =
    typeof pageTitle === 'string' && pageTitle.trim()
      ? pageTitle
      : isReadOnlyViewMode
        ? readOnlyModeLabel
        : content.summaryLink;
  const formIdForUsers = selectedOrLoadedFormId || '';
  const approvalScrollTargetId = `${approvalId}-approval-top`;
  const showApproverForwardQuestion =
    isOptionalApproval && reviewMode && isApprovalActor && isApproval;
  const showForwardApprovalSubmitControls =
    showApproverForwardQuestion && approverForwardChoice === 'yes';
  const shouldFetchApproverUsersForSubmission = Boolean(
    approvalFlowEnabled &&
      formIdForUsers &&
      !approvalFixedEmail &&
      !isReadOnlyViewMode &&
      ((formState === 'active' && !isApprovalActor) ||
        (formState === 'approval' && showForwardApprovalSubmitControls) ||
        formState === 'rejected'),
  );
  const shouldFetchApproverUsersForDisplay = Boolean(showApprovalRequestSummary && formIdForUsers);

  const { data: formUsersData, isFetched: isFormUsersFetched } = useQuery({
    queryKey: ['form-users', formIdForUsers],
    enabled: shouldFetchApproverUsersForSubmission || shouldFetchApproverUsersForDisplay,
    queryFn: async () => {
      const { idToken } = await fetchJWT();
      if (!idToken || !formIdForUsers) return { access: [] };
      return fetchFormUsers(idToken, { form_id: formIdForUsers });
    },
    staleTime: 0,
    retry: false,
  });

  const allApproverUsers = useMemo(() => {
    return (formUsersData?.access || []).filter((entry) => {
      if (String(entry.role || '').toLowerCase() !== 'approver') return false;
      return Boolean(String(entry.email || '').trim());
    });
  }, [formUsersData?.access]);
  const approverUsers = useMemo(() => {
    const currentUserId = String(formUsersData?.currentUserId || '');
    return allApproverUsers.filter(
      (entry) => !(currentUserId && String(entry.userId || '') === currentUserId),
    );
  }, [allApproverUsers, formUsersData?.currentUserId]);


  const selectedApproverEmail = useMemo(() => {
    if (approvalApproverEmail) return approvalApproverEmail;
    if (submittedApproverEmail) return submittedApproverEmail;
    if (hasAdminApprovalTarget) return 'Admin';
    if (approvalFixedEmail) return approvalFixedEmail;

    const currentUserId = String(formUsersData?.currentUserId || '');
    if (isApprovalActor && currentUserId) {
      const currentApprover = allApproverUsers.find(
        (entry) => String(entry.userId || '') === currentUserId,
      );
      if (currentApprover?.email) return String(currentApprover.email);
    }

    if (allApproverUsers.length === 1) {
      return String(allApproverUsers[0].email || '');
    }

    return '';
  }, [
    allApproverUsers,
    approvalApproverEmail,
    approvalFixedEmail,
    formUsersData?.currentUserId,
    hasAdminApprovalTarget,
    isApprovalActor,
    submittedApproverEmail,
  ]);
  const isActiveAuthor = formState === 'active' && !isApprovalActor && !isReadOnlyViewMode;
  const isRejectedAuthor =
    formState === 'rejected' && isEditableRole && !isApprovalActor && !isReadOnlyViewMode;
  const showUnlockNotice = isSubmitted && !hasApprover && isEditableRole && !isPreviewMode;
  const showTableauLicenseKeys =
    tenant === 'tableau' && isSubmitted && isEditableRole && !isPreviewMode;
  const showOptionalApprovalQuestion = isOptionalApproval && isActiveAuthor;
  const isPsfaOptionalApprovalQuestion = tenant === 'psfa' && showOptionalApprovalQuestion;
  const optionalApprovalNeedsApprover = showOptionalApprovalQuestion && (
    isPsfaOptionalApprovalQuestion
      ? PSFA_APPROVAL_ROUTE_REQUIRES_APPROVER.has(approvalRequestChoice)
      : approvalRequestChoice === 'yes'
  );
  const optionalApprovalDirectSubmit = showOptionalApprovalQuestion && (
    isPsfaOptionalApprovalQuestion
      ? approvalRequestChoice === PSFA_APPROVAL_ROUTE_REVIEW
      : approvalRequestChoice === 'no'
  );

  useEffect(() => {
    if (
      !showOptionalApprovalQuestion ||
      isPsfaOptionalApprovalQuestion ||
      !isFormUsersFetched ||
      approvalRequestChoice
    ) {
      return;
    }
    setApprovalRequestChoice(approverUsers.length > 0 ? 'yes' : 'no');
  }, [
    approvalRequestChoice,
    approverUsers.length,
    isFormUsersFetched,
    isPsfaOptionalApprovalQuestion,
    showOptionalApprovalQuestion,
  ]);
  const showApprovalSubmitControls =
    !isSubmissionDisabledInPreview &&
    !isIncompleteSummaryView &&
    ((requiresApproval && isActiveAuthor) || optionalApprovalNeedsApprover);
  const showResubmitForApprovalControls =
    !isSubmissionDisabledInPreview &&
    !isIncompleteSummaryView &&
    approvalFlowEnabled &&
    isRejectedAuthor;
  const showAnyApprovalSubmitControls =
    showApprovalSubmitControls || showResubmitForApprovalControls;
  const showAnyApprovalRequestControls =
    showAnyApprovalSubmitControls || showForwardApprovalSubmitControls;
  useEffect(() => {
    if (
      approvalFixedEmail ||
      !showAnyApprovalRequestControls ||
      !isFormUsersFetched ||
      approverUsers.length > 0 ||
      selectedApprover
    ) {
      return;
    }
    setSelectedApprover('other');
  }, [
    approvalFixedEmail,
    approverUsers.length,
    isFormUsersFetched,
    selectedApprover,
    showAnyApprovalRequestControls,
  ]);
  const showDeclarationLink =
    !isSubmissionDisabledInPreview &&
    !isIncompleteSummaryView &&
    !isEditableApprovalBeforeClose &&
    !showAnyApprovalSubmitControls &&
    (!showOptionalApprovalQuestion || optionalApprovalDirectSubmit);
  const hasDeclarationQuestions = useMemo(
    () =>
      sections.some((section) =>
        section.subsections.some((subsection) =>
          subsection.pages.some((page) => {
            if (!('questions' in page) || !Array.isArray(page.questions)) return false;
            return page.questions.some((question) => {
              const questionName = String(question.name || '').toLowerCase();
              const questionTitle = String(question.title || '').toLowerCase();
              return questionName.includes('declaration') || questionTitle.includes('declaration');
            });
          }),
        ),
      ),
    [sections],
  );
  const showApprovalSubmitHeading =
    requiresApproval && formState === 'active' && !isApprovalActor && !isReadOnlyRole;
  const showDeclarationHeading = showApprovalSubmitHeading || hasDeclarationQuestions;
  const canSubmitFromSummary =
    !isViewOnly &&
    !isEditableApprovalBeforeClose &&
    !isIncompleteSummaryView;
  const showSubmitScrollLink = !reviewMode && !isSubmissionDisabledInPreview && canSubmitFromSummary;
  const showIncompleteSummaryBanner = isIncompleteCheckMode && isIncompleteSummaryView;
  const incompleteSummaryMessage = 'Please answer questions to submit this form';
  const hasDeclarationContent =
    reviewMode ||
    isViewOnly ||
    isEditableApprovalBeforeClose ||
    !canSubmitFromSummary ||
    showOptionalApprovalQuestion ||
    showAnyApprovalSubmitControls ||
    showDeclarationLink;
  const submitScrollTargetId = `${approvalId}-submit-top`;

  const areAllSectionsOpen = useMemo(() => {
    if (visibleSummarySubsectionSlugs.length === 0) return false;
    return visibleSummarySubsectionSlugs.every((slug) => openSections.includes(slug));
  }, [openSections, visibleSummarySubsectionSlugs]);

  useEffect(() => {
    if (!isIncompleteCheckMode || !isIncompleteSummaryView) return;
    if (visibleSummarySubsectionSlugs.length === 0) return;
    setOpenSections(visibleSummarySubsectionSlugs);
  }, [isIncompleteCheckMode, isIncompleteSummaryView, visibleSummarySubsectionSlugs]);

  const handleToggleAllSections = () => {
    setOpenSections(areAllSectionsOpen ? [] : visibleSummarySubsectionSlugs);
  };

  const handleToggleSection = (subsectionSlug: string) => {
    setOpenSections((prev) =>
      prev.includes(subsectionSlug)
        ? prev.filter((slug) => slug !== subsectionSlug)
        : [...prev, subsectionSlug],
    );
  };

  const renderSummaryTotals = (
    pagesForSummary: typeof visibleSummarySubsections[number]['pagesForSummary'],
    subsectionSlug: string,
  ) => {
    const answerValues = data?.answers || {};
    const formValues = data || ({ answers: {} } as FetchFormResponse);
    const totals = pagesForSummary
      .map((page) => {
        if (isApprovalPageName(page.name) || page.name === 'review') return null;
        if (isPageDisabled(page, formValues)) return null;
        if (!('questions' in page) || !page.total) return null;

        const totalRows = renderListTotals(answerValues, page.questions || [], page.total);
        if (!totalRows) return null;

        return (
          <table className="govuk-table govuk-!-margin-bottom-4" key={page.name}>
            <tbody className="govuk-table__body">{totalRows}</tbody>
          </table>
        );
      })
      .filter(Boolean);

    if (totals.length === 0) return null;

    return <div data-testid={`summary-subsection-totals-${subsectionSlug}`}>{totals}</div>;
  };

  useEffect(() => {
    if (data?.answers) {
      const approvalDefaultAnswers = { ...(data.answers as Record<string, unknown>) };
      if (tenant === 'reward') {
        [
          'approval-reward-type',
          'approval-iyb-amount',
          'approval-reward-voucher-amount',
          'approval-reward-voucher-other-amount',
          'approval-reward-reason',
        ].forEach((fieldName) => {
          if (typeof approvalDefaultAnswers[fieldName] !== 'undefined') return;
          const defaultValue = getRewardApprovalDefaultValue(fieldName, approvalDefaultAnswers);
          if (typeof defaultValue !== 'undefined') {
            approvalDefaultAnswers[fieldName] = defaultValue;
          }
        });
      }
      approvalMethods.reset(approvalDefaultAnswers);
    }
  }, [data?.answers, approvalMethods, tenant]);

  useEffect(() => {
    setRewardRemovedTeamNomineeIds([]);
    setRewardAddedTeamNominees([]);
    setRewardRemovedAddedTeamNomineeIds([]);
    setIsRewardAddNomineeModalOpen(false);
    setRewardAddNomineeError('');
    approvalMethods.unregister([
      REWARD_TEAM_EDIT_CHOICE_FIELD,
      REWARD_TEAM_EDIT_REASON_FIELD,
      REWARD_TEAM_REMOVED_FIELD,
      REWARD_TEAM_ADDED_FIELD,
      ...REWARD_ADD_NOMINEE_FIELD_NAMES,
    ]);
  }, [approvalMethods, selectedOrLoadedFormId]);

  useEffect(() => {
    if (!showRewardTeamPeopleEdit) {
      setRewardRemovedTeamNomineeIds([]);
      setRewardAddedTeamNominees([]);
      setRewardRemovedAddedTeamNomineeIds([]);
      approvalMethods.unregister([
        REWARD_TEAM_EDIT_CHOICE_FIELD,
        REWARD_TEAM_EDIT_REASON_FIELD,
        REWARD_TEAM_REMOVED_FIELD,
        REWARD_TEAM_ADDED_FIELD,
      ]);
      return;
    }

    if (watchedRewardTeamEditChoice !== 'Yes') {
      setRewardRemovedTeamNomineeIds([]);
      setRewardAddedTeamNominees([]);
      setRewardRemovedAddedTeamNomineeIds([]);
      approvalMethods.unregister([
        REWARD_TEAM_EDIT_REASON_FIELD,
        REWARD_TEAM_REMOVED_FIELD,
        REWARD_TEAM_ADDED_FIELD,
      ]);
    }
  }, [approvalMethods, showRewardTeamPeopleEdit, watchedRewardTeamEditChoice]);

  useEffect(() => {
    if (isRewardAddNomineeModalOpen) return;
    setRewardAddNomineeError('');
    approvalMethods.unregister([...REWARD_ADD_NOMINEE_FIELD_NAMES]);
  }, [approvalMethods, isRewardAddNomineeModalOpen]);

  const selectedApprovalGroupParam = new URLSearchParams(location.search || '').get('g') || '';
  const formParams = selectedOrLoadedFormId
    ? {
        form_id: selectedOrLoadedFormId,
        ...(selectedApprovalGroupParam ? { g: selectedApprovalGroupParam } : {}),
      }
    : selectedApprovalGroupParam
      ? { g: selectedApprovalGroupParam }
      : undefined;
  const scrollToApprovalSection = () => {
    const target = document.getElementById(approvalScrollTargetId);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const scrollToSubmitSection = () => {
    const target = document.getElementById(submitScrollTargetId);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const clearRewardAddNomineeValues = () => {
    REWARD_ADD_NOMINEE_FIELD_NAMES.forEach((fieldName) => {
      approvalMethods.setValue(fieldName, '', { shouldDirty: false, shouldValidate: false });
    });
    approvalMethods.clearErrors([...REWARD_ADD_NOMINEE_FIELD_NAMES]);
  };

  const serializeRewardTeamNominees = (records: RewardTeamNomineeRecord[]) =>
    records.map((record) => ({
      id: record.id,
      source: record.source,
      name: record.name,
      staff_number: record.staffNumber,
      email: record.email,
      manager_email: record.managerEmail,
      cost_centre: record.costCentre,
      grade: record.grade,
      function_name: record.functionName,
    }));

  const getRewardTeamEditApprovalPayload = () => {
    const removedNominees = rewardOriginalTeamNominees.filter((record) =>
      rewardRemovedTeamNomineeIds.includes(record.id),
    );

    return {
      [REWARD_TEAM_REMOVED_FIELD]: JSON.stringify(serializeRewardTeamNominees(removedNominees)),
      [REWARD_TEAM_ADDED_FIELD]: JSON.stringify(
        serializeRewardTeamNominees(activeRewardAddedTeamNominees),
      ),
    };
  };

  const openRewardAddNomineeModal = () => {
    clearRewardAddNomineeValues();
    setRewardAddNomineeError('');
    setIsRewardAddNomineeModalOpen(true);
  };

  const closeRewardAddNomineeModal = () => {
    setIsRewardAddNomineeModalOpen(false);
    setRewardAddNomineeError('');
    clearRewardAddNomineeValues();
  };

  const handleRewardAddNomineeConfirm = async () => {
    setRewardAddNomineeError('');
    const isValid = await approvalMethods.trigger([
      REWARD_ADD_NOMINEE_LOOKUP_FIELD,
      REWARD_ADD_NOMINEE_FULL_NAME_FIELD,
    ]);
    if (!isValid) return;

    const values = approvalMethods.getValues();
    const name = getStringValue(values[REWARD_ADD_NOMINEE_FULL_NAME_FIELD]);
    const email = getStringValue(values[REWARD_ADD_NOMINEE_EMAIL_FIELD]).toLowerCase();
    if (!name) {
      setRewardAddNomineeError('Select a person before adding them');
      return;
    }

    const activeEmails = new Set(
      [
        ...rewardOriginalTeamNominees.filter(
          (record) => !rewardRemovedTeamNomineeIds.includes(record.id),
        ),
        ...activeRewardAddedTeamNominees,
      ]
        .map((record) => record.email)
        .filter(Boolean),
    );
    if (email && activeEmails.has(email)) {
      setRewardAddNomineeError('This person is already included in this nomination');
      return;
    }

    const addedNominee: RewardTeamNomineeRecord = {
      id: `added-${Date.now()}-${rewardAddedTeamNominees.length + 1}`,
      source: 'added',
      name,
      email,
      staffNumber: getStringValue(values[REWARD_ADD_NOMINEE_STAFF_NUMBER_FIELD]),
      managerEmail: getStringValue(values[REWARD_ADD_NOMINEE_MANAGER_EMAIL_FIELD]).toLowerCase(),
      costCentre: getStringValue(values[REWARD_ADD_NOMINEE_COST_CENTRE_FIELD]),
      grade: getStringValue(values[REWARD_ADD_NOMINEE_GRADE_FIELD]),
      functionName: getStringValue(values[REWARD_ADD_NOMINEE_FUNCTION_FIELD]),
    };

    setRewardAddedTeamNominees((current) => [...current, addedNominee]);
    setRewardRemovedAddedTeamNomineeIds((current) =>
      current.filter((id) => id !== addedNominee.id),
    );
    closeRewardAddNomineeModal();
  };

  const { mutateAsync: submitForApproval, isPending: isSubmittingForApproval } = useMutation({
    mutationFn: async (payload: {
      action?: 'submit' | 'forward';
      notes: string;
      approverEmail?: string;
      approvalQuarter?: string;
      approvalYear?: string;
      approvalRoute?: string;
    }) => {
      const { idToken } = await fetchJWT();
      if (!idToken) throw new Error('Not authenticated');
      await approveForm(
        idToken,
        {
          action: payload.action || 'submit',
          notes: payload.notes,
          approverEmail: payload.approverEmail,
          approvalQuarter: payload.approvalQuarter,
          approvalYear: payload.approvalYear,
          approvalRoute: payload.approvalRoute,
        },
        formParams,
      );
      await queryClient.invalidateQueries({
        queryKey: [Query.FormData],
      });
      await queryClient.refetchQueries({
        queryKey: [Query.FormData],
      });
      return true;
    },
  });

  const { mutateAsync: withdrawApproval } = useMutation({
    mutationFn: async (payload: { reason?: string }) => {
      const { idToken } = await fetchJWT();
      if (!idToken) throw new Error('Not authenticated');
      await unsubmitForm(
        idToken,
        {
          reason: payload.reason,
        },
        formParams,
      );
      await queryClient.invalidateQueries({
        queryKey: [Query.FormData],
      });
      await queryClient.refetchQueries({
        queryKey: [Query.FormData],
      });
      setSelectedFormIdInSession(tenant, selectedOrLoadedFormId);
      navigate(indexPathWithFormId);
      return true;
    },
  });

  const { mutateAsync: submitReview, isPending: isSubmittingReview } = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { idToken } = await fetchJWT();
      if (!idToken) throw new Error('Not authenticated');
      await approveForm(idToken, payload, formParams);
      await queryClient.invalidateQueries({
        queryKey: [Query.FormData],
      });
      await queryClient.refetchQueries({
        queryKey: [Query.FormData],
      });
      return true;
    },
  });

  const submitApprovalDecision = async (
    values: ApprovalDecisionValues,
    options: SubmitApprovalDecisionOptions = {},
  ) => {
    const decision = String(values.approvalDecision || '').toLowerCase();
    const shouldSubmitRewardTeamPeopleEdits = decision === 'yes' && showRewardTeamPeopleEdit;
    let valuesForSubmission: ApprovalDecisionValues = { ...values };
    if (shouldSubmitRewardTeamPeopleEdits && watchedRewardTeamEditChoice === 'Yes') {
      if (effectiveRewardTeamNominees.length < 2) {
        approvalMethods.setError(REWARD_TEAM_EDIT_CHOICE_FIELD, {
          type: 'manual',
          message: 'Team nomination must have at least 2 people after edits',
        });
        return;
      }
      valuesForSubmission = {
        ...valuesForSubmission,
        ...getRewardTeamEditApprovalPayload(),
      };
    }

    if (
      tenant === 'reward' &&
      decision === 'yes' &&
      rewardBadgeModalForCurrentForm &&
      !options.rewardBadgeAcknowledged
    ) {
      setPendingRewardBadgeModal({
        ...rewardBadgeModalForCurrentForm,
        approvalValues: { ...valuesForSubmission },
      });
      return;
    }

    if (tenant === 'reward' && decision === 'yes' && !options.rewardApprovalConfirmed) {
      const answers = ((data as any)?.answers || {}) as Record<string, unknown>;
      const nomineeNamesForWarnings =
        shouldSubmitRewardTeamPeopleEdits && watchedRewardTeamEditChoice === 'Yes'
          ? effectiveRewardTeamNomineeNames
          : undefined;
      const limitWarningRows = options.rewardApprovalLimitConfirmed
        ? []
        : getRewardApprovalLimitWarningRows(
            valuesForSubmission,
            answers,
            contextTables,
            nomineeNamesForWarnings,
          );

      if (limitWarningRows.length > 0) {
        setPendingRewardApprovalLimitWarningValues({ ...valuesForSubmission });
        setRewardApprovalLimitWarningRows(limitWarningRows);
        return;
      }

      setPendingRewardApprovalValues({ ...valuesForSubmission });
      setHasConfirmedRewardApproval(false);
      return;
    }

    const comments = String(valuesForSubmission.approvalComment || '').trim();
    const approvalAnswers =
      approvalPageEntry && approvalQuestionNames.length > 0
        ? approvalQuestionNames.reduce(
            (result, name) => {
              const value = (valuesForSubmission as any)[name];
              if (typeof value !== 'undefined') {
                result[name] = value;
              }
              return result;
            },
            {} as Record<string, unknown>,
          )
        : {};
    if (shouldSubmitRewardTeamPeopleEdits) {
      const editChoice = String(valuesForSubmission[REWARD_TEAM_EDIT_CHOICE_FIELD] || '').trim();
      if (editChoice) {
        approvalAnswers[REWARD_TEAM_EDIT_CHOICE_FIELD] = editChoice;
      }
      if (editChoice === 'Yes') {
        approvalAnswers[REWARD_TEAM_EDIT_REASON_FIELD] =
          valuesForSubmission[REWARD_TEAM_EDIT_REASON_FIELD];
        approvalAnswers[REWARD_TEAM_REMOVED_FIELD] =
          valuesForSubmission[REWARD_TEAM_REMOVED_FIELD] || '[]';
        approvalAnswers[REWARD_TEAM_ADDED_FIELD] =
          valuesForSubmission[REWARD_TEAM_ADDED_FIELD] || '[]';
      }
    }

    const shouldSaveApprovalAnswersBeforeDecision = tenant !== 'reward';
    if (
      shouldSaveApprovalAnswersBeforeDecision &&
      approvalPageEntry &&
      approvalQuestionNames.length > 0
    ) {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('Not authenticated');
      await updateForm(
        token,
        {
          'subsection-id': approvalPageEntry.subsection.slug,
          'page-id': approvalPageEntry.page.name,
          'is-save-unfinished': false,
          answers: approvalAnswers,
        },
        formParams,
      );
    }

    try {
      await submitReview({
        action: decision === 'no' ? 'reject' : 'approve',
        comments: comments || undefined,
        approvalQuarter: approvalQuarter || undefined,
        approvalYear: approvalYear || undefined,
        approvalAnswers,
      });
      setSelectedFormIdInSession(tenant, null);
      navigate(returnToFormsPath);
    } catch (error) {
      const message = error instanceof Error ? error.message : content.errorApi;
      if (message === content.approvalNoLongerAwaiting) {
        approvalMethods.setError('approvalDecision', {
          type: 'manual',
          message,
        });
        return;
      }
      throw error;
    }
  };

  const handleApprovalDecisionSubmit = async (values: ApprovalDecisionValues) =>
    submitApprovalDecision(values);

  const handleRewardApprovalLimitWarningContinue = async () => {
    if (!pendingRewardApprovalLimitWarningValues) return;

    const approvalValues = pendingRewardApprovalLimitWarningValues;
    setPendingRewardApprovalLimitWarningValues(null);
    setRewardApprovalLimitWarningRows([]);
    await submitApprovalDecision(approvalValues, {
      rewardApprovalLimitConfirmed: true,
    });
  };

  const handleRewardApprovalConfirm = async () => {
    if (!pendingRewardApprovalValues || !hasConfirmedRewardApproval) return;

    const approvalValues = pendingRewardApprovalValues;
    flushSync(() => {
      setPendingRewardApprovalValues(null);
      setHasConfirmedRewardApproval(false);
      setIsRewardApprovalConfirmSubmitting(true);
    });
    await waitForNextFrame();
    try {
      await submitApprovalDecision(approvalValues, {
        rewardBadgeAcknowledged: true,
        rewardApprovalConfirmed: true,
      });
    } finally {
      setIsRewardApprovalConfirmSubmitting(false);
    }
  };

  const handleRewardBadgeNice = async () => {
    if (!pendingRewardBadgeModal?.approvalValues) {
      setPendingRewardBadgeModal(null);
      return;
    }

    const approvalValues = pendingRewardBadgeModal.approvalValues;
    flushSync(() => {
      setPendingRewardBadgeModal(null);
    });
    await waitForNextFrame();
    await submitApprovalDecision(approvalValues, {
      rewardBadgeAcknowledged: true,
    });
  };

  const handleApprovalRequest = async (action: 'submit' | 'forward' = 'submit') => {
    setApproverSelectionError(null);
    setApproverEmailError(null);
    setApprovalSubmitError(null);

    let approverEmail: string | undefined = approvalFixedEmail || undefined;
    if (!approvalFixedEmail && approverUsers.length > 0) {
      if (!selectedApprover) {
        setApproverSelectionError('Please select an approver');
        return false;
      }

      if (selectedApprover !== 'other') {
        approverEmail = selectedApprover.trim().toLowerCase();
      }
    }

    if (
      !approvalFixedEmail &&
      (selectedApprover === 'other' || approverUsers.length === 0)
    ) {
      const validationResult = validateField(
        'Approver email',
        { required: true, email: true },
        otherApproverEmail,
      );
      if (validationResult) {
        setApproverEmailError(validationResult);
        return false;
      }
      approverEmail = otherApproverEmail.trim().toLowerCase();
    }

    setSubmittedApproverEmail(approverEmail || '');
    const approvalRoute =
      action === 'submit' &&
      isPsfaOptionalApprovalQuestion &&
      PSFA_APPROVAL_ROUTE_REQUIRES_APPROVER.has(approvalRequestChoice)
        ? approvalRequestChoice
        : undefined;
    try {
      await submitForApproval({
        action,
        notes: approvalNotes,
        approverEmail,
        approvalQuarter: approvalQuarter || undefined,
        approvalYear: approvalYear || undefined,
        approvalRoute,
      });
      return true;
    } catch (error) {
      setApprovalSubmitError(resolveMutationErrorMessage(error));
      return false;
    }
  };

  const triggerFileDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const buildRewardHistorySummary = async (
    token: string,
    formId: string,
    submittedAt?: string,
  ) => {
    const formResponse = await fetchForm(token, { form_id: formId });
    const answers =
      formResponse?.answers && typeof formResponse.answers === 'object'
        ? (formResponse.answers as Record<string, unknown>)
        : {};
    const drafts =
      formResponse?.drafts && typeof formResponse.drafts === 'object'
        ? (formResponse.drafts as Record<string, string[]>)
        : {};
    const enabledSubsectionsForExport = getEnabledSubsections(formConfig, formResponse);
    const submittedAtForExport =
      typeof formResponse?.submittedAt === 'string' && formResponse.submittedAt.trim()
        ? formResponse.submittedAt
        : typeof (formResponse as any)?.submitted_at === 'string' &&
            String((formResponse as any).submitted_at).trim()
          ? String((formResponse as any).submitted_at).trim()
          : submittedAt;

    return {
      html: buildExportHtml(
        tenant,
        formConfig?.title,
        submittedAtForExport,
        formConfig?.sections || [],
        answers,
        drafts,
        enabledSubsectionsForExport,
        formId,
        formResponse?.rewardPanelDecision,
        formResponse?.rewardPanelNotes,
      ),
      formVersion: formResponse?.formVersion || (formResponse as any)?.form_version,
    };
  };

  const handleHistoryQuickView = async (row: ContextTableRow) => {
    const formId = String(row.formId || '').trim();
    if (!formId) return;

    setHistoryDownloadError('');
    setRewardHistoryQuickView({ formId, html: '', error: '', isComparisonView: false });

    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');

      const submittedAt = String(row.submittedAt || row.submitted_at || '').trim() || undefined;
      const { html } = await buildRewardHistorySummary(token, formId, submittedAt);
      setRewardHistoryQuickView((current) =>
        current?.formId === formId ? { ...current, html } : current,
      );
    } catch (error) {
      console.error(`Reward history quick view failed for form [${formId}]`, error);
      setRewardHistoryQuickView((current) =>
        current?.formId === formId
          ? {
              ...current,
              error: buildDownloadErrorMessage(
                'Unable to load this nomination right now.',
                error,
              ),
            }
          : current,
      );
    }
  };

  const handleHistoryDownload = async (
    row: Record<string, string | number | boolean | null | undefined>,
    selectedFormat: 'excel' | 'html' | 'pdf',
  ) => {
    const formId = String(row.formId || '').trim();
    const submittedAt = String(row.submittedAt || row.submitted_at || '').trim() || undefined;
    const activeDownloadKey = `${formId}-${selectedFormat}`;

    if (!formId) {
      setHistoryDownloadError('Unable to download this form right now.');
      return;
    }

    setHistoryDownloadError('');
    setActiveHistoryDownload(activeDownloadKey);

    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');

      const filenameBase = toExportFilenameBase(formConfig?.title || '', 'answers');

      if (selectedFormat === 'excel') {
        const { blob, filename } = await downloadExportExcel(token, { form_id: formId });
        triggerFileDownload(blob, filename || `${filenameBase}.xlsx`);
        return;
      }

      const { html, formVersion } = await buildRewardHistorySummary(token, formId, submittedAt);

      if (selectedFormat === 'html') {
        const { blob, filename } = await downloadExportRenderedHtml(token, {
          html,
          filename: filenameBase,
          formVersion,
        });
        triggerFileDownload(blob, filename || `${filenameBase}.html`);
        return;
      }

      const { blob, filename } = await convertExportHtmlToPdf(token, {
        html,
        filename: filenameBase,
        formVersion,
      });
      triggerFileDownload(blob, filename || `${filenameBase}.pdf`);
    } catch (error) {
      console.error(`Reward history download failed for form [${formId}]`, error);
      setHistoryDownloadError(
        buildDownloadErrorMessage('Unable to download this form right now.', error),
      );
    } finally {
      setActiveHistoryDownload('');
    }
  };

  const renderRewardCurrentNomination = () => {
    if (!showRewardNominationIdentifier) return null;

    return (
      <div className="govuk-!-margin-top-6 govuk-!-margin-bottom-6">
        <p className="govuk-body govuk-!-margin-bottom-2">
          <strong>Current nomination:</strong> {rewardNominationIdentifier}
        </p>
        {rewardTeamNomineeNames.length > 0 && (
          <ul className="govuk-list govuk-list--bullet">
            {rewardTeamNomineeNames.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const applyRewardApprovalDefaultsToQuestion = (question: Question): Question => {
    if (tenant !== 'reward' || activeApprovalPageName !== 'approval') return question;

    const answers = ((data as any)?.answers || {}) as Record<string, unknown>;
    const defaultValue = getRewardApprovalDefaultValue(question.name, answers);
    const options = Array.isArray(question.options)
      ? question.options.map((option: SelectOption) => {
          if (typeof option === 'string') return option;
          if ('option' in option) {
            return {
              ...option,
              questions: option.questions?.map((nested) =>
                applyRewardApprovalDefaultsToQuestion(nested),
              ),
            };
          }
          return option;
        })
      : question.options;

    return {
      ...question,
      ...(defaultValue ? { defaultValue } : {}),
      options,
    };
  };

  const getRewardApprovalTeamSplitNotice = (): { message: string; note?: string } | null => {
    if (tenant !== 'reward' || activeApprovalPageName !== 'approval') return null;
    if (String(approvalMethods.watch('approval-change-nomination') || '') !== 'Yes') return null;

    const answers = ((data as any)?.answers || {}) as Record<string, unknown>;
    if (String(answers['nomination-kind'] || '').trim().toLowerCase() !== 'team') return null;

    const teamNomineeCount = shouldApplyRewardTeamPeopleEdits
      ? effectiveRewardTeamNominees.length
      : getRewardTeamNomineeNames(answers).length;
    if (teamNomineeCount < 1) return null;

    const defaultAwardType = getRewardApprovalDefaultValue('approval-reward-type', answers);
    const awardType = String(approvalMethods.watch('approval-reward-type') || defaultAwardType || '');

    if (awardType === 'In Year Bonus') {
      const defaultAmount = getRewardApprovalDefaultValue('approval-iyb-amount', answers);
      const rawAmount = approvalMethods.watch('approval-iyb-amount') || defaultAmount;
      const totalAmount = parseCurrencyAmount(rawAmount);
      if (totalAmount === null) return null;

      const perPersonAmount = roundDownToTwoDecimalPlaces(totalAmount / teamNomineeCount);
      return {
        message: `Each team member will receive \u00A3${formatCurrencyAmount(perPersonAmount)} (before tax)`,
      };
    }

    if (awardType === 'Voucher') {
      const defaultSelectedAmount = getRewardApprovalDefaultValue(
        'approval-reward-voucher-amount',
        answers,
      );
      const defaultOtherAmount = getRewardApprovalDefaultValue(
        'approval-reward-voucher-other-amount',
        answers,
      );
      const selectedAmount =
        approvalMethods.watch('approval-reward-voucher-amount') || defaultSelectedAmount;
      const rawAmount =
        selectedAmount === 'Other'
          ? approvalMethods.watch('approval-reward-voucher-other-amount') || defaultOtherAmount
          : selectedAmount;
      const totalAmount = parseCurrencyAmount(rawAmount);
      if (totalAmount === null) return null;

      const perPersonAmount = roundDownToNearestFive(totalAmount / teamNomineeCount);
      return {
        message: `Each team member will receive a \u00A3${formatCurrencyAmount(perPersonAmount)} voucher`,
        note:
          '(Thank you e-vouchers are held in denominations of \u00A35, so the total voucher amount is split per team member with that in mind)',
      };
    }

    return null;
  };

  const renderRewardTeamNomineeEditRow = (
    record: RewardTeamNomineeRecord,
    removed: boolean,
    onToggle: () => void,
  ) => (
    <li
      className="govuk-!-margin-bottom-2"
      key={`${record.source}-${record.id}`}
      style={{
        alignItems: 'center',
        display: 'flex',
        gap: 12,
        justifyContent: 'space-between',
      }}
    >
      <span
        className="govuk-body govuk-!-margin-bottom-0"
        style={{ textDecoration: removed ? 'line-through' : 'none' }}
      >
        {record.name}
        {record.email && (
          <span className="govuk-hint govuk-!-margin-bottom-0"> {record.email}</span>
        )}
      </span>
      <button
        className={`govuk-button govuk-!-margin-bottom-0 ${
          removed ? 'govuk-button--secondary' : 'govuk-button--warning'
        }`}
        data-module="govuk-button"
        onClick={onToggle}
        type="button"
      >
        {removed ? 'Undo' : 'Remove'}
      </button>
    </li>
  );

  const renderRewardTeamPeopleEditor = () => {
    if (!showRewardTeamPeopleEdit) return null;

    return (
      <div className="govuk-!-margin-top-4">
        <div
          className={`govuk-form-group ${
            approvalMethods.formState.errors[REWARD_TEAM_EDIT_CHOICE_FIELD]
              ? 'govuk-form-group--error'
              : ''
          }`}
        >
          <Fieldset
            title={
              <OptionalLabel
                text={rewardTeamEditChoiceQuestion.title}
                optional={false}
              />
            }
            legendId={`${approvalId}${REWARD_TEAM_EDIT_CHOICE_FIELD}-legend`}
          >
            <RadioGroup
              question={rewardTeamEditChoiceQuestion}
              anchorId={`${approvalId}${REWARD_TEAM_EDIT_CHOICE_FIELD}`}
              id={approvalId}
            />
          </Fieldset>
        </div>

        {watchedRewardTeamEditChoice === 'Yes' && (
          <div className="govuk-!-margin-top-4">
            <h3 className="govuk-heading-s">People on this nomination</h3>
            <ul className="govuk-list govuk-!-margin-bottom-4">
              {rewardOriginalTeamNominees.map((record) =>
                renderRewardTeamNomineeEditRow(
                  record,
                  rewardRemovedTeamNomineeIds.includes(record.id),
                  () => {
                    setRewardRemovedTeamNomineeIds((current) =>
                      current.includes(record.id)
                        ? current.filter((id) => id !== record.id)
                        : [...current, record.id],
                    );
                  },
                ),
              )}
              {rewardAddedTeamNominees.map((record) =>
                renderRewardTeamNomineeEditRow(
                  record,
                  rewardRemovedAddedTeamNomineeIds.includes(record.id),
                  () => {
                    setRewardRemovedAddedTeamNomineeIds((current) =>
                      current.includes(record.id)
                        ? current.filter((id) => id !== record.id)
                        : [...current, record.id],
                    );
                  },
                ),
              )}
            </ul>
            <button
              className="govuk-button govuk-!-margin-bottom-6"
              data-module="govuk-button"
              onClick={openRewardAddNomineeModal}
              type="button"
            >
              Add new person
            </button>
            <div
              className={`govuk-form-group ${
                approvalMethods.formState.errors[REWARD_TEAM_EDIT_REASON_FIELD]
                  ? 'govuk-form-group--error'
                  : ''
              }`}
            >
              <label
                className="govuk-label govuk-label--s"
                htmlFor={`${approvalId}${REWARD_TEAM_EDIT_REASON_FIELD}`}
              >
                Reasons for edit
              </label>
              <Textarea
                id={`${approvalId}${REWARD_TEAM_EDIT_REASON_FIELD}`}
                name={REWARD_TEAM_EDIT_REASON_FIELD}
                title="Reasons for edit"
                textRows={4}
                rules={{ required: true }}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderRewardAddNomineeModal = () => {
    if (!isRewardAddNomineeModalOpen) return null;

    return (
      <div className="feedback-modal-backdrop" role="presentation">
        <div
          aria-modal="true"
          aria-labelledby="reward-add-nominee-modal-title"
          className="feedback-modal govuk-!-padding-6"
          role="dialog"
          style={{
            maxHeight: '90vh',
            maxWidth: 760,
            overflowY: 'auto',
          }}
        >
          <h2
            className="govuk-heading-m govuk-!-margin-bottom-4"
            id="reward-add-nominee-modal-title"
          >
            Add new person
          </h2>
          {rewardAddNomineeError && (
            <p className="govuk-error-message">{rewardAddNomineeError}</p>
          )}
          <AthenaStaffLookup
            question={rewardAddNomineeLookupQuestion}
            anchorId={`${approvalId}${REWARD_ADD_NOMINEE_LOOKUP_FIELD}`}
          />
          <TextInputField
            id={`${approvalId}${REWARD_ADD_NOMINEE_FULL_NAME_FIELD}`}
            name={REWARD_ADD_NOMINEE_FULL_NAME_FIELD}
            title="Nominee full name"
            readOnly
            rules={{ required: true }}
          />
          <TextInputField
            id={`${approvalId}${REWARD_ADD_NOMINEE_EMAIL_FIELD}`}
            name={REWARD_ADD_NOMINEE_EMAIL_FIELD}
            title="Email address of the nominee"
            readOnly
          />
          <TextInputField
            id={`${approvalId}${REWARD_ADD_NOMINEE_STAFF_NUMBER_FIELD}`}
            name={REWARD_ADD_NOMINEE_STAFF_NUMBER_FIELD}
            title="Nominee SOP staff number"
            readOnly
          />
          <TextInputField
            id={`${approvalId}${REWARD_ADD_NOMINEE_COST_CENTRE_FIELD}`}
            name={REWARD_ADD_NOMINEE_COST_CENTRE_FIELD}
            title="Cost Centre"
            readOnly
          />
          <TextInputField
            id={`${approvalId}${REWARD_ADD_NOMINEE_GRADE_FIELD}`}
            name={REWARD_ADD_NOMINEE_GRADE_FIELD}
            title="Substantive Grade of the nominee"
            readOnly
          />
          <TextInputField
            id={`${approvalId}${REWARD_ADD_NOMINEE_FUNCTION_FIELD}`}
            name={REWARD_ADD_NOMINEE_FUNCTION_FIELD}
            title="Nominee function"
            readOnly
          />
          <TextInputField
            id={`${approvalId}${REWARD_ADD_NOMINEE_MANAGER_EMAIL_FIELD}`}
            name={REWARD_ADD_NOMINEE_MANAGER_EMAIL_FIELD}
            title="Nominee line manager email address"
            readOnly
          />
          <div className="govuk-button-group govuk-!-margin-top-4">
            <button
              className="govuk-button"
              data-module="govuk-button"
              onClick={handleRewardAddNomineeConfirm}
              type="button"
            >
              Add
            </button>
            <button
              className="govuk-button govuk-button--secondary"
              data-module="govuk-button"
              onClick={closeRewardAddNomineeModal}
              type="button"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderApprovalQuestion = (question: Question) => {
    const configuredQuestion = applyRewardApprovalDefaultsToQuestion(question);
    if (configuredQuestion.questionType === 'reportPeriod') {
      return null;
    }

    const errorMessage = approvalMethods.formState.errors[configuredQuestion.name];
    const decision = String(approvalMethods.watch('approvalDecision') || '').toLowerCase();
    const shouldEnforceInjectedRequired = decision !== 'no';
    const effectiveQuestion = shouldEnforceInjectedRequired
      ? configuredQuestion
      : toOptionalApprovalQuestion(configuredQuestion);
    const rules = shouldEnforceInjectedRequired
      ? effectiveQuestion?.rules || {}
      : {
          ...(effectiveQuestion?.rules || {}),
          required: false,
        };
    const hideOptional = !!effectiveQuestion.hideOptionalLabel;
    const anchorId = `${approvalId}${effectiveQuestion.name}`;
    const legendId = `${anchorId}-legend`;
    const rewardApprovalTeamSplitNotice =
      effectiveQuestion.name === 'approval-change-nomination'
        ? getRewardApprovalTeamSplitNotice()
        : null;

    return (
      <div
        className={`govuk-form-group ${errorMessage ? 'govuk-form-group--error' : ''}`}
        key={effectiveQuestion.name}
      >
        <Fieldset
          title={
            <OptionalLabel
              text={effectiveQuestion.title}
              optional={!(rules?.required === true || hideOptional)}
            />
          }
          hintText={effectiveQuestion.hint}
          legendId={legendId}
        >
          <>
            {effectiveQuestion.questionType === 'textInput' && (
              <TextInputField
                id={anchorId}
                name={effectiveQuestion.name}
                title={effectiveQuestion.title}
                rules={rules}
                placeholder={effectiveQuestion.placeholder}
                wordCount={effectiveQuestion.wordCount}
                characterCount={effectiveQuestion.characterCount}
                errorMessages={effectiveQuestion.errorMessages}
                detailsHeading={effectiveQuestion.detailsHeading}
                detailsContent={effectiveQuestion.detailsContent}
              />
            )}
            {effectiveQuestion.questionType === 'textarea' && (
              <Textarea
                id={anchorId}
                name={effectiveQuestion.name}
                title={effectiveQuestion.title}
                rules={rules}
                numeric={!!effectiveQuestion.rules?.number || undefined}
                placeholder={effectiveQuestion.placeholder}
                wordCount={effectiveQuestion.wordCount}
                characterCount={effectiveQuestion.characterCount}
                textRows={effectiveQuestion.textRows}
                errorMessages={effectiveQuestion.errorMessages}
                detailsHeading={effectiveQuestion.detailsHeading}
                detailsContent={effectiveQuestion.detailsContent}
              />
            )}
            {effectiveQuestion.questionType === 'select' && (
              <SelectInputField
                id={anchorId}
                name={effectiveQuestion.name}
                title={effectiveQuestion.title}
                options={Array.isArray(effectiveQuestion.options) ? effectiveQuestion.options : []}
                hintOption={effectiveQuestion.hintOption}
                rules={rules}
                errorMessages={effectiveQuestion.errorMessages}
                detailsHeading={effectiveQuestion.detailsHeading}
                detailsContent={effectiveQuestion.detailsContent}
                defaultValue={
                  typeof effectiveQuestion.defaultValue === 'string'
                    ? effectiveQuestion.defaultValue
                    : undefined
                }
              />
            )}
            {effectiveQuestion.questionType === 'radio' && (
              <>
                <RadioGroup
                  question={effectiveQuestion}
                  anchorId={anchorId}
                  id={approvalId}
                  detailsHeading={effectiveQuestion.detailsHeading}
                  detailsContent={effectiveQuestion.detailsContent}
                />
                {rewardApprovalTeamSplitNotice && (
                  <div className="govuk-!-margin-top-4">
                    <p
                      className={`govuk-body govuk-!-font-weight-bold ${
                        rewardApprovalTeamSplitNotice.note
                          ? 'govuk-!-margin-bottom-1'
                          : 'govuk-!-margin-bottom-3'
                      }`}
                    >
                      {rewardApprovalTeamSplitNotice.message}
                    </p>
                    {rewardApprovalTeamSplitNotice.note && (
                      <p className="govuk-body govuk-!-margin-top-0 govuk-!-margin-bottom-3">
                        {rewardApprovalTeamSplitNotice.note}
                      </p>
                    )}
                  </div>
                )}
                {effectiveQuestion.name === 'approval-change-nomination' &&
                  renderRewardTeamPeopleEditor()}
              </>
            )}
            {effectiveQuestion.questionType === 'checkbox' && (
              <CheckboxGroup
                question={effectiveQuestion}
                anchorId={anchorId}
                id={approvalId}
                detailsHeading={effectiveQuestion.detailsHeading}
                detailsContent={effectiveQuestion.detailsContent}
              />
            )}
            {effectiveQuestion.questionType === 'date' && (
              <DateInputField
                question={effectiveQuestion}
                anchorId={anchorId}
                detailsHeading={effectiveQuestion.detailsHeading}
                detailsContent={effectiveQuestion.detailsContent}
              />
            )}
            {effectiveQuestion.questionType === 'shortDate' && (
              <ShortDateInputField
                question={effectiveQuestion}
                anchorId={anchorId}
                detailsHeading={effectiveQuestion.detailsHeading}
                detailsContent={effectiveQuestion.detailsContent}
              />
            )}
            {effectiveQuestion.questionType === 'autoComplete' && (
              <AutocompleteWrapper
                id={anchorId}
                items={effectiveQuestion.items || []}
                options={Array.isArray(effectiveQuestion.options) ? effectiveQuestion.options : []}
                name={effectiveQuestion.name}
                title={effectiveQuestion.title}
                rules={rules}
                detailsHeading={effectiveQuestion.detailsHeading}
                detailsContent={effectiveQuestion.detailsContent}
              />
            )}
            {![
              'textInput',
              'textarea',
              'select',
              'radio',
              'checkbox',
              'date',
              'shortDate',
              'autoComplete',
            ].includes(question.questionType) && (
              <p className="govuk-body">Unsupported question type: {question.questionType}</p>
            )}
          </>
        </Fieldset>
      </div>
    );
  };

  const isFormContextResolved = Boolean(formState) && role.length > 0;
  if (isReviewRequested && isFormContextResolved && !reviewMode) {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('review');
    const nextSearch = searchParams.toString();
    const tenantBasePath = tenant ? `/${tenant}` : '';
    const summaryPath = `${tenantBasePath}/${config.urls.summary}`;
    return <Navigate to={`${summaryPath}${nextSearch ? `?${nextSearch}` : ''}`} replace />;
  }

  const buildCurrentNominationSummary = () =>
    buildExportHtml(
      tenant,
      formConfig?.title,
      typeof data?.submittedAt === 'string' ? data.submittedAt : undefined,
      formConfig?.sections || [],
      data?.answers || {},
      data?.drafts || {},
      enabledSubsections,
      selectedOrLoadedFormId,
      data?.rewardPanelDecision,
      data?.rewardPanelNotes,
    );

  return (
    <>
      {rewardHistoryQuickView && (
        <div
          className={`feedback-modal-backdrop${
            rewardHistoryQuickView.isComparisonView
              ? ' reward-history-quick-view-backdrop--comparison'
              : ''
          }`}
          role="presentation"
        >
          <div
            aria-modal="true"
            aria-labelledby="reward-history-quick-view-title"
            className={`reward-history-quick-view${
              rewardHistoryQuickView.isComparisonView
                ? ' reward-history-quick-view--comparison'
                : ''
            }`}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setRewardHistoryQuickView(null);
            }}
            role="dialog"
          >
            <div className="reward-history-quick-view__header">
              <h2
                className="govuk-heading-l govuk-!-margin-bottom-0"
                id="reward-history-quick-view-title"
              >
                {rewardHistoryQuickView.isComparisonView
                  ? 'Nomination comparison'
                  : 'Nomination quick view'}
              </h2>
              <div className="reward-history-quick-view__actions">
                <button
                  aria-pressed={rewardHistoryQuickView.isComparisonView}
                  className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                  onClick={() =>
                    setRewardHistoryQuickView((current) =>
                      current ? { ...current, isComparisonView: !current.isComparisonView } : null,
                    )
                  }
                  type="button"
                >
                  {rewardHistoryQuickView.isComparisonView
                    ? 'Exit comparison view'
                    : 'Compare side by side'}
                </button>
                <button
                  aria-label="Close quick view"
                  className="reward-history-quick-view__close"
                  onClick={() => setRewardHistoryQuickView(null)}
                  ref={rewardHistoryQuickViewCloseRef}
                  type="button"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            </div>
            <div
              className={`reward-history-quick-view__body${
                rewardHistoryQuickView.isComparisonView
                  ? ' reward-history-quick-view__body--comparison'
                  : ''
              }`}
            >
              {rewardHistoryQuickView.isComparisonView && (
                <section className="reward-history-quick-view__pane">
                  <h3 className="govuk-heading-m">Current nomination</h3>
                  <iframe
                    className="reward-history-quick-view__frame"
                    sandbox=""
                    srcDoc={buildCurrentNominationSummary()}
                    title="Current nomination summary"
                  />
                </section>
              )}
              <section className="reward-history-quick-view__pane">
                {rewardHistoryQuickView.isComparisonView && (
                  <h3 className="govuk-heading-m">Historic nomination</h3>
                )}
                {rewardHistoryQuickView.error ? (
                  <p className="govuk-error-message" role="alert">
                    <span className="govuk-visually-hidden">Error:</span>{' '}
                    {rewardHistoryQuickView.error}
                  </p>
                ) : rewardHistoryQuickView.html ? (
                  <iframe
                    className="reward-history-quick-view__frame"
                    sandbox=""
                    srcDoc={rewardHistoryQuickView.html}
                    title="Historic nomination summary"
                  />
                ) : (
                  <p aria-live="polite" className="govuk-body">
                    Loading nomination summary...
                  </p>
                )}
              </section>
            </div>
          </div>
        </div>
      )}
      {pendingRewardBadgeModal && (
        <div className="feedback-modal-backdrop" role="presentation">
          <div
            aria-modal="true"
            aria-labelledby="reward-badge-modal-title"
            className="feedback-modal govuk-!-padding-6"
            role="dialog"
            style={{ maxWidth: 640 }}
          >
            <h2 className="govuk-visually-hidden" id="reward-badge-modal-title">
              Reward badge
            </h2>
            <div className="reward-badge-modal__image-wrap">
              <img
                alt={pendingRewardBadgeModal.altText}
                className="reward-badge-modal__image"
                src={pendingRewardBadgeModal.imageSrc}
              />
              <span className="reward-badge-modal__sparkle reward-badge-modal__sparkle--one" />
              <span className="reward-badge-modal__sparkle reward-badge-modal__sparkle--two" />
              <span className="reward-badge-modal__sparkle reward-badge-modal__sparkle--three" />
              <span className="reward-badge-modal__sparkle reward-badge-modal__sparkle--four" />
            </div>
            <div className="reward-badge-modal__actions">
              <button
                className="govuk-button govuk-!-margin-bottom-0"
                data-module="govuk-button"
                onClick={handleRewardBadgeNice}
                type="button"
              >
                Nice
              </button>
            </div>
          </div>
        </div>
      )}
      {approvalRedirectCountdown !== null && (
        <div className="feedback-modal-backdrop" role="presentation">
          <div
            aria-live="polite"
            aria-modal="true"
            className="feedback-modal govuk-!-padding-6"
            role="dialog"
          >
            <h2 className="govuk-heading-m govuk-!-margin-bottom-2">
              Form has been sent for approval ({approvalRedirectCountdown})
            </h2>
            <p className="govuk-body govuk-!-margin-bottom-0">Returning to My Forms.</p>
          </div>
        </div>
      )}
      {pendingRewardApprovalLimitWarningValues && rewardApprovalLimitWarningRows.length > 0 && (
        <div className="feedback-modal-backdrop" role="presentation">
          <div
            aria-modal="true"
            aria-labelledby="reward-approval-limit-warning-title"
            className="feedback-modal govuk-!-padding-6"
            role="dialog"
          >
            <h2
              className="govuk-heading-m govuk-!-margin-bottom-4"
              id="reward-approval-limit-warning-title"
            >
              Recommended award limit exceeded
            </h2>
            <p className="govuk-body">
              {rewardApprovalLimitWarningRows.length === 1
                ? `This nomination will take ${rewardApprovalLimitWarningRows[0].name} over their annual recommended award limit of \u00A32000. Please confirm you wish to Continue?`
                : 'This nomination will take the nominees listed below over their annual recommended award limit of \u00A32000. Please confirm you wish to Continue?'}
            </p>
            <table className="govuk-table">
              <thead className="govuk-table__head">
                <tr className="govuk-table__row">
                  <th className="govuk-table__header" scope="col">
                    Nominee
                  </th>
                  <th className="govuk-table__header" scope="col">
                    Current approved
                  </th>
                  <th className="govuk-table__header" scope="col">
                    This award
                  </th>
                  <th className="govuk-table__header" scope="col">
                    Projected
                  </th>
                </tr>
              </thead>
              <tbody className="govuk-table__body">
                {rewardApprovalLimitWarningRows.map((row) => (
                  <tr className="govuk-table__row" key={row.name}>
                    <td className="govuk-table__cell">{row.name}</td>
                    <td className="govuk-table__cell">
                      {formatCurrencyWithCommas(row.approvedTotal)}
                    </td>
                    <td className="govuk-table__cell">
                      {formatCurrencyWithCommas(row.awardAmount)}
                    </td>
                    <td
                      className="govuk-table__cell govuk-!-font-weight-bold"
                      style={{ color: '#d4351c' }}
                    >
                      {formatCurrencyWithCommas(row.projectedTotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="govuk-button-group">
              <button
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                disabled={isSubmittingReview}
                onClick={() => {
                  setPendingRewardApprovalLimitWarningValues(null);
                  setRewardApprovalLimitWarningRows([]);
                }}
                type="button"
              >
                Cancel
              </button>
              <button
                className="govuk-button"
                data-module="govuk-button"
                disabled={isSubmittingReview}
                onClick={handleRewardApprovalLimitWarningContinue}
                type="button"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      {pendingRewardApprovalValues && (
        <div className="feedback-modal-backdrop" role="presentation">
          <div
            aria-modal="true"
            aria-labelledby="reward-approval-confirmation-title"
            className="feedback-modal govuk-!-padding-6"
            role="dialog"
          >
            <h2
              className="govuk-heading-m govuk-!-margin-bottom-4"
              id="reward-approval-confirmation-title"
            >
              Confirm approval
            </h2>
            <div className="govuk-form-group">
              <div className="govuk-checkboxes" data-module="govuk-checkboxes">
                <div className="govuk-checkboxes__item">
                  <input
                    checked={hasConfirmedRewardApproval}
                    className="govuk-checkboxes__input"
                    id="reward-approval-confirmation"
                    name="reward-approval-confirmation"
                    onChange={(event) => setHasConfirmedRewardApproval(event.target.checked)}
                    type="checkbox"
                  />
                  <label
                    className="govuk-label govuk-checkboxes__label"
                    htmlFor="reward-approval-confirmation"
                  >
                    I confirm that this nomination has been discussed with the function panel and
                    has been approved.
                  </label>
                </div>
              </div>
            </div>
            <p className="govuk-body">
              When you click approve, you are confirming that the award should now be processed and
              issued to the staff member.
            </p>
            <div className="govuk-button-group">
              <button
                className="govuk-button"
                data-module="govuk-button"
                disabled={
                  !hasConfirmedRewardApproval || isSubmittingReview || isRewardApprovalConfirmSubmitting
                }
                onClick={handleRewardApprovalConfirm}
                type="button"
              >
                Approve
              </button>
              <button
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                disabled={isSubmittingReview || isRewardApprovalConfirmSubmitting}
                onClick={() => {
                  setPendingRewardApprovalValues(null);
                  setHasConfirmedRewardApproval(false);
                }}
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {isLoading ? (
        <PageTemplate title="">{null}</PageTemplate>
      ) : (
        <PageTemplate
          title={pageTitle}
          limitWidth={limitSummaryWidth}
          forceLoadingOverlay={isRewardApprovalConfirmSubmitting}
          titleClassName={
            [
              showApprovalScrollLink || showSubmitScrollLink ? 'gdad-tight-title' : '',
              isReadOnlyViewMode ? 'govuk-!-margin-bottom-1' : '',
            ]
              .filter(Boolean)
              .join(' ') || undefined
          }
          beforeTitle={
            showRewardTransferBanner ||
            showBanner ||
            showRejectionNoteBanner ||
            showApprovalWithdrawnNoteBanner ||
            showSubmitterNoteBanner ||
            showIncompleteSummaryBanner ? (
              <>
                {showRewardTransferBanner && (
                  <Notification
                    header="Transferred nomination"
                    content={`This nomination was transferred to your panel by the ${escapeHtml(
                      rewardTransferOriginalFunction,
                    )} function`}
                  />
                )}
                {showIncompleteSummaryBanner && (
                  <Notification
                    content={incompleteSummaryMessage}
                    className="govuk-notification-banner--success"
                  />
                )}
                {showSubmitterNoteBanner && (
                  <Notification
                    header={
                      approvalRequesterEmail
                        ? `Note from submitter (${approvalRequesterEmail})`
                        : 'Note from submitter'
                    }
                    content={`Note: ${approvalRequestNote ? escapeHtml(approvalRequestNote) : 'None'}`}
                  />
                )}
                {showBanner && (
                  <Notification
                    header={bannerHeader}
                    content={bannerContent}
                    className={
                      isSubmitted
                        ? 'govuk-notification-banner--success'
                        : 'govuk-notification-banner--error'
                    }
                  />
                )}
                {showRejectionNoteBanner && (
                  <Notification
                    header="The approver rejected this form"
                    content={
                      rejectionNote
                        ? `Please update this form and re-submit.<br />Note from approver: ${escapeHtml(
                            rejectionNote,
                          )}`
                        : 'Please update this form and re-submit.'
                    }
                    className="govuk-notification-banner--error"
                  />
                )}
                {showApprovalWithdrawnNoteBanner && (
                  <Notification
                    header={
                      approvalWithdrawnByEmail
                        ? `Approval request withdrawn (${approvalWithdrawnByEmail})`
                        : 'Approval request withdrawn'
                    }
                    content={`Note: ${escapeHtml(approvalWithdrawnNote)}`}
                  />
                )}
              </>
            ) : undefined
          }
          breadcrumbs={
            isReadOnlyViewMode || (!isSubmitted && !showBanner)
              ? { sectionName: breadcrumbEndLabel }
              : undefined
          }
          fullWidth
        >
          {renderRewardCurrentNomination()}
          {showSubmitScrollLink && (
            <p className="govuk-body govuk-!-margin-top-0 govuk-!-margin-bottom-6">
              <a
                href={`#${submitScrollTargetId}`}
                className="govuk-link"
                onClick={(event) => {
                  event.preventDefault();
                  scrollToSubmitSection();
                }}
              >
                Once you have checked your answers you can submit this form at the bottom of this
                page
              </a>
            </p>
          )}
          {isReadOnlyViewMode && (
            <>
              <p className="govuk-body govuk-!-margin-top-0 govuk-!-margin-bottom-1">
                {readOnlyModeLabel}
              </p>
              <p className="govuk-body govuk-!-margin-top-0 govuk-!-margin-bottom-4">
                <span className={`govuk-tag ${getStatusTagClass(formStatusLabel)}`}>
                  {formStatusLabel}
                </span>
              </p>
            </>
          )}
          {showUnlockNotice && (
            <UnlockNotice
              allowUnlock={formConfig?.allowUnlock}
              submittedAt={(data as any)?.submittedAt}
              formCloseAt={formConfig?.formCloseAt ?? (data as any)?.formCloseAt}
              formId={selectedOrLoadedFormId}
            />
          )}
          {showTableauLicenseKeys && (
            <section className="govuk-!-margin-top-6 govuk-!-margin-bottom-6">
              <h2 className="govuk-heading-m">Your Tableau licence key(s)</h2>
              <TableauLicenseKeys formId={selectedOrLoadedFormId} />
            </section>
          )}
          {historyDownloadError && (
            <p className="govuk-error-message" role="alert">
              <span className="govuk-visually-hidden">Error:</span> {historyDownloadError}
            </p>
          )}
          {showRewardHistoryFilters && (
            <div className="govuk-!-margin-top-6 govuk-!-margin-bottom-4">
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'end' }}>
                <div className="govuk-form-group govuk-!-margin-bottom-0">
                  <label className="govuk-label" htmlFor={`${approvalId}-reward-history-type`}>
                    Reward type
                  </label>
                  <select
                    className="govuk-select"
                    id={`${approvalId}-reward-history-type`}
                    name="reward-history-type"
                    value={selectedRewardHistoryRewardType}
                    onChange={(event) => setSelectedRewardHistoryRewardType(event.target.value)}
                  >
                    <option value="">All reward types</option>
                    {rewardHistoryFilterOptions.rewardTypes.map((rewardType) => (
                      <option value={rewardType} key={rewardType}>
                        {rewardType}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="govuk-form-group govuk-!-margin-bottom-0">
                  <label className="govuk-label" htmlFor={`${approvalId}-reward-history-function`}>
                    Approver function
                  </label>
                  <select
                    className="govuk-select"
                    id={`${approvalId}-reward-history-function`}
                    name="reward-history-function"
                    value={selectedRewardHistoryFunction}
                    onChange={(event) => setSelectedRewardHistoryFunction(event.target.value)}
                  >
                    <option value="">All functions</option>
                    {rewardHistoryFilterOptions.functions.map((rewardFunction) => (
                      <option value={rewardFunction} key={rewardFunction}>
                        {rewardFunction}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="govuk-checkboxes govuk-checkboxes--small govuk-!-margin-bottom-0">
                  <div className="govuk-checkboxes__item">
                    <input
                      className="govuk-checkboxes__input"
                      id={`${approvalId}-reward-history-pending`}
                      name="reward-history-pending"
                      type="checkbox"
                      checked={showRewardHistoryPendingOnly}
                      onChange={(event) => setShowRewardHistoryPendingOnly(event.target.checked)}
                    />
                    <label
                      className="govuk-label govuk-checkboxes__label"
                      htmlFor={`${approvalId}-reward-history-pending`}
                    >
                      Pending approval only
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
          <ContextTables
            tables={filteredContextTables}
            tableIds={formConfig.approvalContextTables?.tableIds}
            showSectionBreak={formConfig.approvalContextTables?.showSectionBreak}
            renderRowAction={(table, row) => {
              if (table.id !== 'reward-history') return null;
              const historyFormId = String(row.formId || '').trim();
              if (!historyFormId) return null;
              const formats: Array<{ format: 'excel' | 'pdf' | 'html'; icon: HistoryDownloadIcon }> = [
                // { format: 'html', icon: 'html' },
                { format: 'pdf', icon: 'pdf' },
                // { format: 'excel', icon: 'excel' },
              ];
              return (
                <div style={historyDownloadButtonGroupStyle}>
                  <button
                    aria-label="Quick view"
                    className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                    data-module="govuk-button"
                    disabled={activeHistoryDownload.length > 0}
                    onClick={() => {
                      void handleHistoryQuickView(row);
                    }}
                    style={historyDownloadIconButtonStyle}
                    title="Quick view"
                    type="button"
                  >
                    <QuickViewIcon />
                  </button>
                  {formats.map(({ format, icon }) => {
                    const ariaLabel =
                      format === 'excel'
                        ? 'Download Excel'
                        : format === 'pdf'
                          ? 'Download PDF'
                          : 'Download HTML';
                    return (
                      <button
                        className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                        type="button"
                        data-module="govuk-button"
                        aria-label={ariaLabel}
                        title={ariaLabel}
                        style={historyDownloadIconButtonStyle}
                        disabled={activeHistoryDownload.length > 0}
                        key={format}
                        onClick={() => {
                          void handleHistoryDownload(row, format);
                        }}
                      >
                        <DownloadFileIcon icon={icon} />
                      </button>
                    );
                  })}
                </div>
              );
            }}
          />
          {isDownloadableSummaryState && <Download />}
          {showWithdrawApprovalControls && (
            <p className="govuk-body">
              No further action is required. We will email you when the form has been approved or
              rejected.
            </p>
          )}
          {showApprovalScrollLink && (
            <>
              {tenant === 'reward' && isAdmin && isReviewRequested && (
                <h2 className="govuk-heading-m govuk-!-margin-bottom-2">Nomination Details</h2>
              )}
              <p className="govuk-body govuk-!-margin-top-0 govuk-!-margin-bottom-1">
                Below you can see the answers submitted for approval by the form submitter.
              </p>
              <p className="govuk-body govuk-!-margin-top-0 govuk-!-margin-bottom-4">
                <a
                  href={`#${approvalScrollTargetId}`}
                  className="govuk-link"
                  onClick={(event) => {
                    event.preventDefault();
                    scrollToApprovalSection();
                  }}
                >
                  You can approve or reject this request at the bottom of this page
                </a>
                <br />
                <br />
              </p>
            </>
          )}
          {showWithdrawApprovalControls && (
            <div className="govuk-inset-text">
              <p className="govuk-body govuk-!-margin-bottom-1">
                This form was sent for approval to {selectedApproverEmail} on{' '}
                {approvalRequestedText || '-'}.
              </p>
              <p className="govuk-body govuk-!-margin-top-0 govuk-!-margin-bottom-6">
                You can unsubmit this form if it contains an error. This will cancel the approval
                process and the form will need to be re-submitted.
              </p>
              <div className="govuk-form-group">
                <label className="govuk-label" htmlFor={`${approvalId}-withdraw-reason`}>
                  <OptionalLabel text="Unsubmit reason" optional />
                </label>
                <div id={`${approvalId}-withdraw-reason-hint`} className="govuk-hint">
                  This will be visible to the approver
                </div>
                <textarea
                  className="govuk-textarea"
                  id={`${approvalId}-withdraw-reason`}
                  rows={4}
                  aria-describedby={`${approvalId}-withdraw-reason-hint`}
                  value={withdrawReason}
                  onChange={(e) => setWithdrawReason(e.target.value)}
                />
              </div>
              <button
                className="govuk-button govuk-button--warning"
                type="button"
                data-module="govuk-button"
                data-prevent-double-click="true"
                onClick={() =>
                  withdrawApproval({
                    reason: withdrawReason,
                  })
                }
              >
                Unsubmit Form
              </button>
            </div>
          )}
          <div data-testid="overview">
            <span
              className={`govuk-link  govuk-body summary-list__toggle ${
                areAllSectionsOpen ? 'summary-list__toggle--open' : 'summary-list__toggle--closed'
              }`}
              onClick={handleToggleAllSections}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleToggleAllSections();
                }
              }}
            >
              {areAllSectionsOpen ? 'Hide all sections' : 'Show all sections'}
            </span>
            <ol className="govuk-list" data-testid="overview-sectionList">
              {visibleSummarySubsections.map(({ section, subsection, pagesForSummary }) => {
                return (
                  <li key={subsection.slug} className="summary-list__item">
                    <div className="summary-list__item-header">
                      <h3
                        data-testid={`overview-subsectionList-${subsection.slug}Link`}
                        className="govuk-heading-l"
                        onClick={() => handleToggleSection(subsection.slug)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleToggleSection(subsection.slug);
                          }
                        }}
                      >
                        {subsection.title}
                      </h3>
                      <span
                        className={`govuk-link govuk-body summary-list__toggle ${
                          openSections.includes(subsection.slug)
                            ? 'summary-list__toggle--open'
                            : 'summary-list__toggle--closed'
                        }`}
                        onClick={() => handleToggleSection(subsection.slug)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleToggleSection(subsection.slug);
                          }
                        }}
                      >
                        <span className="summary-list__item-header-button-text">
                          {openSections.includes(subsection.slug) ? 'Hide' : 'Show'}
                        </span>
                      </span>
                    </div>
                    {renderSummaryTotals(pagesForSummary, subsection.slug)}
                    <div
                      className={`govuk-body ${
                        openSections.includes(subsection.slug) ? '' : 'govuk-!-display-none'
                      }`}
                    >
                      <SummaryAnswers
                        pages={pagesForSummary}
                        subsection={subsection}
                        urlPath={`/${section.slug}/${subsection.slug}`}
                        data={data}
                        disableChangeLinks={disableSummaryChangeLinks}
                        includeListTotals={false}
                        searchSuffix={previewSearchSuffix}
                      />
                    </div>
                  </li>
                );
              })}
              {showApprovalsSection && (
                <li className="summary-list__item">
                  {!showRewardReviewApprovalNotesOnly && (
                    <h3 className="govuk-heading-l">Approvals</h3>
                  )}
                  {approvalRequestHistoryEntries.length > 0 ? (
                    <>
                      {approvalRequestHistoryEntries.map((entry) => {
                        const requestedText = formatLondonDateTime(entry.requestedAt || undefined);
                        return (
                          <div
                            className="govuk-form-group govuk-!-margin-bottom-4"
                            key={entry.key}
                          >
                            {!showRewardReviewApprovalNotesOnly && (
                              <>
                                <h4 className="govuk-heading-m govuk-!-margin-bottom-2">
                                  {getOrdinal(entry.round)} Approval
                                </h4>
                                <p className="govuk-body govuk-!-margin-bottom-1">
                                  {`Sent to ${entry.approverEmail || 'Not available'}.`}
                                </p>
                                {(entry.requesterEmail || requestedText) && (
                                  <p className="govuk-body govuk-!-margin-bottom-2">
                                    {[entry.requesterEmail ? `Sent by ${entry.requesterEmail}` : '', requestedText ? `Sent on ${requestedText}` : '']
                                      .filter(Boolean)
                                      .join(' ')}
                                  </p>
                                )}
                              </>
                            )}
                            <label
                              className="govuk-label"
                              htmlFor={`${approvalId}-${entry.key}-approval-request-history`}
                            >
                              Notes
                            </label>
                            <textarea
                              {...nonSelectableReadOnlyTextareaProps}
                              className="govuk-textarea"
                              id={`${approvalId}-${entry.key}-approval-request-history`}
                              rows={5}
                              value={entry.notes || 'None'}
                            />
                          </div>
                        );
                      })}
                    </>
                  ) : showApprovalRequestSummary && (
                    <>
                      {!showRewardReviewApprovalNotesOnly && (hasAdminApprovalTarget && tenant === 'reward' ? (
                        <Notification
                          header="Approvals"
                          content="This form was sent to the Function Panel for review."
                          className="govuk-notification-banner--success govuk-!-margin-bottom-4"
                        />
                      ) : (
                        <p className="govuk-body govuk-!-margin-bottom-4">
                          {`This form was sent to ${selectedApproverEmail || 'Not available'}.`}
                        </p>
                      ))}
                      <div className="govuk-form-group govuk-!-margin-bottom-4">
                        <label
                          className="govuk-label"
                          htmlFor={`${approvalId}-approval-notes-summary`}
                        >
                          Notes
                        </label>
                        <textarea
                          {...nonSelectableReadOnlyTextareaProps}
                          className="govuk-textarea"
                          id={`${approvalId}-approval-notes-summary`}
                          rows={5}
                          value={approvalRequestNote || 'None'}
                        />
                      </div>
                    </>
                  )}
                  {showSavedRewardApprovalAnswers && approvalPageEntry && (
                    <section aria-labelledby={`${approvalId}-panel-approval`}>
                      <h4 className="govuk-heading-m" id={`${approvalId}-panel-approval`}>
                        Panel Approval
                      </h4>
                      <SummaryAnswers
                        pages={[approvalPageEntry.page]}
                        subsection={approvalPageEntry.subsection}
                        urlPath={`/${approvalPageEntry.section.slug}/${approvalPageEntry.subsection.slug}`}
                        data={data}
                        disableChangeLinks
                        includeApprovalPage
                      />
                    </section>
                  )}
                  {!showRewardReviewApprovalNotesOnly && approvalHistoryEntries.length > 0 && (
                    <>
                      <h4 className="govuk-heading-m">Approval history</h4>
                      {approvalHistoryEntries.map((entry) => {
                        const historyLabel = [entry.status, entry.year].filter(Boolean).join(' ');
                        const approvedText = formatLondonDateTime(entry.approvedAt || undefined);

                        return (
                          <div
                            className="govuk-form-group govuk-!-margin-bottom-4"
                            key={entry.approvalKey}
                          >
                            <p className="govuk-body govuk-!-font-weight-bold govuk-!-margin-bottom-1">
                              {historyLabel || entry.approvalKey}
                            </p>
                            {approvedText && (
                              <p className="govuk-body govuk-!-margin-bottom-2">
                                Approved on {approvedText}
                              </p>
                            )}
                            <label
                              className="govuk-label"
                              htmlFor={`${approvalId}-${entry.approvalKey}-approval-history`}
                            >
                              Notes
                            </label>
                            <textarea
                              {...nonSelectableReadOnlyTextareaProps}
                              className="govuk-textarea"
                              id={`${approvalId}-${entry.approvalKey}-approval-history`}
                              rows={5}
                              value={entry.comments || 'None'}
                            />
                          </div>
                        );
                      })}
                    </>
                  )}
                </li>
              )}
              {!!sections.length && hasDeclarationContent && (
                <DeclarationWrapper showSections={false} sections={sections}>
                  {reviewMode ? (
                    <>
                      <h3 className="govuk-heading-l" id={approvalScrollTargetId}>
                        {isRewardAdminApprovalRequest
                          ? 'Function Panel Approval'
                          : isReview
                            ? 'Review'
                            : 'Approval'}
                      </h3>
                      {renderRewardCurrentNomination()}
                      <FormProvider {...approvalMethods}>
                        <form
                          onSubmit={approvalMethods.handleSubmit(handleApprovalDecisionSubmit)}
                        >
                          <ErrorSummary
                            errors={approvalMethods.formState.errors}
                            idPrefix={approvalId}
                          />
                          {renderRewardAddNomineeModal()}
                          {showApprovalQuestionsAtTop &&
                            visibleApprovalQuestions.map((question: Question) =>
                              renderApprovalQuestion(question),
                            )}

                          {showApproverForwardQuestion && (
                            <fieldset className="govuk-fieldset govuk-!-margin-bottom-4">
                              <legend className="govuk-fieldset__legend govuk-fieldset__legend--m">
                                Would you like to send this form for another approval?
                              </legend>
                              <div className="govuk-radios">
                                <div className="govuk-radios__item">
                                  <input
                                    className="govuk-radios__input"
                                    id={`${approvalId}-approval-forward-yes`}
                                    name={`${approvalId}-approval-forward`}
                                    type="radio"
                                    value="yes"
                                    checked={approverForwardChoice === 'yes'}
                                    onChange={(e) => {
                                      setApproverForwardChoice(e.target.value);
                                      setApprovalSubmitError(null);
                                    }}
                                  />
                                  <label
                                    className="govuk-label govuk-radios__label"
                                    htmlFor={`${approvalId}-approval-forward-yes`}
                                  >
                                    Yes
                                  </label>
                                </div>
                                <div className="govuk-radios__item">
                                  <input
                                    className="govuk-radios__input"
                                    id={`${approvalId}-approval-forward-no`}
                                    name={`${approvalId}-approval-forward`}
                                    type="radio"
                                    value="no"
                                    checked={approverForwardChoice === 'no'}
                                    onChange={(e) => {
                                      setApproverForwardChoice(e.target.value);
                                      setApprovalSubmitError(null);
                                    }}
                                  />
                                  <label
                                    className="govuk-label govuk-radios__label"
                                    htmlFor={`${approvalId}-approval-forward-no`}
                                  >
                                    No
                                  </label>
                                </div>
                              </div>
                            </fieldset>
                          )}

                          {showForwardApprovalSubmitControls && (
                            <>
                              {approvalSubmitError && (
                                <Notification
                                  header="Error"
                                  content={approvalSubmitError}
                                  className="govuk-notification-banner--error"
                                />
                              )}
                              {!approvalFixedEmail && (
                                <>
                                  <label
                                    className="govuk-label"
                                    htmlFor={`${approvalId}-approver`}
                                  >
                                    Please select an approver
                                  </label>
                                  {approverUsers.length === 0 && (
                                    <div className="govuk-hint">
                                      No approvers are currently linked to this form. Choose
                                      another approver to enter their email address.
                                    </div>
                                  )}
                                  {approverSelectionError && (
                                    <span className="govuk-error-message">
                                      {approverSelectionError}
                                    </span>
                                  )}
                                  <select
                                    className="govuk-select govuk-!-margin-bottom-3"
                                    id={`${approvalId}-approver`}
                                    value={selectedApprover}
                                    onChange={(e) => {
                                      setSelectedApprover(e.target.value);
                                      setApproverSelectionError(null);
                                      setApprovalSubmitError(null);
                                      if (e.target.value !== 'other') {
                                        setApproverEmailError(null);
                                      }
                                    }}
                                  >
                                    <option value="">Select an option</option>
                                    {approverUsers.map((entry) => (
                                      <option key={entry.email} value={entry.email}>
                                        {entry.email}
                                      </option>
                                    ))}
                                    <option value="other">Choose another approver</option>
                                  </select>
                                </>
                              )}
                              {!approvalFixedEmail &&
                                (selectedApprover === 'other' || approverUsers.length === 0) && (
                                <div className="govuk-form-group">
                                  <label
                                    className="govuk-label"
                                    htmlFor={`${approvalId}-approver-email`}
                                  >
                                    Approver email
                                  </label>
                                  {approverEmailError && (
                                    <span className="govuk-error-message">
                                      {approverEmailError}
                                    </span>
                                  )}
                                  <input
                                    className="govuk-input govuk-!-width-two-thirds"
                                    id={`${approvalId}-approver-email`}
                                    type="email"
                                    value={otherApproverEmail}
                                    onChange={(e) => {
                                      setOtherApproverEmail(e.target.value);
                                      setApproverEmailError(null);
                                      setApprovalSubmitError(null);
                                    }}
                                  />
                                </div>
                              )}
                              <label className="govuk-label" htmlFor={`${approvalId}-notes`}>
                                <OptionalLabel text="Notes for approver" optional />
                              </label>
                              <textarea
                                className="govuk-textarea"
                                id={`${approvalId}-notes`}
                                rows={5}
                                value={approvalNotes}
                                onChange={(e) => {
                                  setApprovalNotes(e.target.value);
                                  setApprovalSubmitError(null);
                                }}
                              />
                              <button
                                className="govuk-button govuk-!-margin-top-3"
                                type="button"
                                data-module="govuk-button"
                                data-prevent-double-click="true"
                                disabled={isSubmittingForApproval}
                                onClick={async () => {
                                  const sent = await handleApprovalRequest('forward');
                                  if (sent) {
                                    setSelectedFormIdInSession(tenant, null);
                                    navigate(myFormsPath);
                                  }
                                }}
                              >
                                Send for approval
                              </button>
                            </>
                          )}

                          {(!showApproverForwardQuestion || approverForwardChoice === 'no') && (
                            <>
                          <div
                            className={`govuk-form-group ${
                              approvalMethods.formState.errors.approvalDecision
                                ? 'govuk-form-group--error'
                                : ''
                            }`}
                          >
                            <label
                              className="govuk-label"
                              htmlFor={`${approvalId}approvalDecision`}
                            >
                              {approvalDecisionLabel}
                            </label>
                            <div className="govuk-hint">{parseSafeHtml(approvalDecisionHint)}</div>
                            {approvalMethods.formState.errors.approvalDecision && (
                              <span className="govuk-error-message">
                                {String(approvalMethods.formState.errors.approvalDecision.message)}
                              </span>
                            )}
                            <select
                              className="govuk-select"
                              id={`${approvalId}approvalDecision`}
                              {...approvalMethods.register('approvalDecision', {
                                required: 'Select Approve or Reject',
                              })}
                            >
                              <option value="">Select</option>
                              <option value="yes">Approve</option>
                              <option value="no">Reject</option>
                            </select>
                          </div>

                          <div
                            className={`govuk-form-group ${
                              approvalMethods.formState.errors.approvalComment
                                ? 'govuk-form-group--error'
                                : ''
                            }`}
                          >
                            <label className="govuk-label" htmlFor={`${approvalId}approvalComment`}>
                              {String(approvalMethods.watch('approvalDecision') || '') === 'no'
                                ? 'Please provide a rejection reason'
                                : <OptionalLabel text="Notes" optional />}
                            </label>
                            <div id={`${approvalId}approvalCommentHint`} className="govuk-hint">
                              {parseSafeHtml(approvalCommentHint)}
                            </div>
                            {approvalMethods.formState.errors.approvalComment && (
                              <span className="govuk-error-message">
                                {String(approvalMethods.formState.errors.approvalComment.message)}
                              </span>
                            )}
                            <textarea
                              className="govuk-textarea"
                              id={`${approvalId}approvalComment`}
                              rows={5}
                              aria-describedby={`${approvalId}approvalCommentHint`}
                              {...approvalMethods.register('approvalComment', {
                                validate: (value) =>
                                  String(approvalMethods.getValues('approvalDecision') || '') ===
                                  'no'
                                    ? String(value || '').trim().length > 0 ||
                                      'Enter a rejection reason'
                                    : true,
                              })}
                            />
                          </div>

                          {showApprovalQuestionsAtBottom &&
                            visibleApprovalQuestions.map((question: Question) =>
                              renderApprovalQuestion(question),
                            )}

                          <button
                            className="govuk-button"
                            type="submit"
                            data-module="govuk-button"
                            data-prevent-double-click="true"
                          >
                            Submit
                          </button>
                            </>
                          )}
                        </form>
                      </FormProvider>
                    </>
                  ) : (
                    <>
                      {showDeclarationHeading && (
                        <h3 className="govuk-heading-l" id={submitScrollTargetId}>
                          {showApprovalSubmitHeading ? content.submitForApprovalLink : 'Declaration'}
                        </h3>
                      )}
                      <li className="summary-list__item">
                        {isEditableApprovalBeforeClose ? (
                          <Link
                            className="govuk-link"
                            to={myFormsPath}
                            onClick={() => setSelectedFormIdInSession(tenant, null)}
                          >
                            Go to My Forms
                          </Link>
                        ) : isViewOnly ? (
                          <Link
                            className="govuk-link"
                            to={returnToFormsPath}
                            onClick={() => setSelectedFormIdInSession(tenant, null)}
                          >
                            {returnToFormsLabel}
                          </Link>
                        ) : showIncompleteSummaryBanner ? (
                          <Link
                            className="govuk-link"
                            to={indexPathWithFormId}
                            onClick={() =>
                              setSelectedFormIdInSession(tenant, selectedOrLoadedFormId)
                            }
                          >
                            {incompleteSummaryMessage}
                          </Link>
                        ) : !canSubmitFromSummary ? (
                          <div className="govuk-link">
                            {requiresApproval &&
                            formState === 'active' &&
                            !isApprovalActor &&
                            !isReadOnlyRole
                              ? content.submitForApprovalLink
                              : content.submitQuestionnaireLink}
                          </div>
                        ) : (
                          <div className="govuk-form-group">
                            {showOptionalApprovalQuestion && (
                              isPsfaOptionalApprovalQuestion ? (
                                <div className="govuk-form-group govuk-!-margin-bottom-4">
                                  <label
                                    className="govuk-label govuk-label--m"
                                    htmlFor={`${approvalId}-approval-next-step`}
                                  >
                                    Please select the next step
                                  </label>
                                  {approvalRequestHint && (
                                    <div
                                      className="govuk-hint"
                                      id={`${approvalId}-approval-next-step-hint`}
                                    >
                                      {parseSafeHtml(approvalRequestHint)}
                                    </div>
                                  )}
                                  <select
                                    className="govuk-select"
                                    id={`${approvalId}-approval-next-step`}
                                    value={approvalRequestChoice}
                                    aria-describedby={
                                      approvalRequestHint
                                        ? `${approvalId}-approval-next-step-hint`
                                        : undefined
                                    }
                                    onChange={(e) => {
                                      setApprovalRequestChoice(e.target.value);
                                      setApprovalSubmitError(null);
                                    }}
                                  >
                                    <option value="">Select an option</option>
                                    {psfaApprovalNextStepOptions.map((option) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              ) : (
                                <fieldset className="govuk-fieldset govuk-!-margin-bottom-4">
                                  <legend className="govuk-fieldset__legend govuk-fieldset__legend--m">
                                    Would you like to send this form for approval?
                                  </legend>
                                  {approvalRequestHint && (
                                    <div className="govuk-hint">
                                      {parseSafeHtml(approvalRequestHint)}
                                    </div>
                                  )}
                                  <div className="govuk-radios">
                                    <div className="govuk-radios__item">
                                      <input
                                        className="govuk-radios__input"
                                        id={`${approvalId}-approval-request-yes`}
                                        name={`${approvalId}-approval-request`}
                                        type="radio"
                                        value="yes"
                                        checked={approvalRequestChoice === 'yes'}
                                        onChange={(e) => setApprovalRequestChoice(e.target.value)}
                                      />
                                      <label
                                        className="govuk-label govuk-radios__label"
                                        htmlFor={`${approvalId}-approval-request-yes`}
                                      >
                                        Yes
                                      </label>
                                    </div>
                                    <div className="govuk-radios__item">
                                      <input
                                        className="govuk-radios__input"
                                        id={`${approvalId}-approval-request-no`}
                                        name={`${approvalId}-approval-request`}
                                        type="radio"
                                        value="no"
                                        checked={approvalRequestChoice === 'no'}
                                        onChange={(e) => setApprovalRequestChoice(e.target.value)}
                                      />
                                      <label
                                        className="govuk-label govuk-radios__label"
                                        htmlFor={`${approvalId}-approval-request-no`}
                                      >
                                        No
                                      </label>
                                    </div>
                                  </div>
                                </fieldset>
                              )
                            )}

                            {showAnyApprovalSubmitControls && (
                              <>
                                {approvalSubmitError && (
                                  <Notification
                                    header="Error"
                                    content={approvalSubmitError}
                                    className="govuk-notification-banner--error"
                                  />
                                )}
                                {approvalFixedEmail && (
                                  <p className="govuk-body">
                                    {hasAdminApprovalTarget
                                      ? tenant === 'reward'
                                        ? 'This form will be sent to the Function Panel for approval.'
                                        : 'This form will be placed into admin approval.'
                                      : `This form will be sent to ${approvalFixedEmail} for approval.`}
                                  </p>
                                )}
                                {!approvalFixedEmail && (
                                  <>
                                    <label
                                      className="govuk-label"
                                      htmlFor={`${approvalId}-approver`}
                                    >
                                      Please select an approver
                                    </label>
                                    {approverUsers.length === 0 && (
                                      <div className="govuk-hint">
                                        No approvers are currently linked to this form. Choose
                                        another approver to enter their email address.
                                      </div>
                                    )}
                                    {approverSelectionError && (
                                      <span className="govuk-error-message">
                                        {approverSelectionError}
                                      </span>
                                    )}
                                    <select
                                      className="govuk-select govuk-!-margin-bottom-3"
                                      id={`${approvalId}-approver`}
                                      value={selectedApprover}
                                      onChange={(e) => {
                                        setSelectedApprover(e.target.value);
                                        setApproverSelectionError(null);
                                        setApprovalSubmitError(null);
                                        if (e.target.value !== 'other') {
                                          setApproverEmailError(null);
                                        }
                                      }}
                                    >
                                      <option value="">Select an option</option>
                                      {approverUsers.map((entry) => (
                                        <option key={entry.email} value={entry.email}>
                                          {entry.email}
                                        </option>
                                      ))}
                                      <option value="other">Choose another approver</option>
                                    </select>
                                  </>
                                )}
                                {!approvalFixedEmail &&
                                  (selectedApprover === 'other' || approverUsers.length === 0) && (
                                  <div className="govuk-form-group">
                                    <label
                                      className="govuk-label"
                                      htmlFor={`${approvalId}-approver-email`}
                                    >
                                      Approver email
                                    </label>
                                    {approverEmailError && (
                                      <span className="govuk-error-message">
                                        {approverEmailError}
                                      </span>
                                    )}
                                    <input
                                      className="govuk-input govuk-!-width-two-thirds"
                                      id={`${approvalId}-approver-email`}
                                      type="email"
                                      value={otherApproverEmail}
                                      onChange={(e) => {
                                        setOtherApproverEmail(e.target.value);
                                        setApproverEmailError(null);
                                        setApprovalSubmitError(null);
                                      }}
                                    />
                                  </div>
                                )}
                                <label className="govuk-label" htmlFor={`${approvalId}-notes`}>
                                  <OptionalLabel text="Notes for approver" optional />
                                </label>
                                {approvalRequestHint && !showOptionalApprovalQuestion && (
                                  <div className="govuk-hint">
                                    {parseSafeHtml(approvalRequestHint)}
                                  </div>
                                )}
                                <textarea
                                  className="govuk-textarea"
                                  id={`${approvalId}-notes`}
                                  rows={5}
                                  value={approvalNotes}
                                  onChange={(e) => {
                                    setApprovalNotes(e.target.value);
                                    setApprovalSubmitError(null);
                                  }}
                                />
                                <button
                                  className="govuk-button govuk-!-margin-top-3"
                                  type="button"
                                  data-module="govuk-button"
                                  data-prevent-double-click="true"
                                  disabled={isSubmittingForApproval}
                                  onClick={async () => {
                                    const sent = await handleApprovalRequest('submit');
                                    if (sent && tenant === 'scs') {
                                      setApprovalRedirectCountdown(2);
                                    }
                                  }}
                                >
                                  {showResubmitForApprovalControls
                                    ? 'Resubmit for approval'
                                    : content.submitForApprovalLink}
                                </button>
                              </>
                            )}

                            {showDeclarationLink && (
                              <Link
                                className="govuk-link"
                                to={`/${tenant}/${config.urls.declaration}${location.search || ''}`}
                              >
                                {content.submitQuestionnaireLink}
                              </Link>
                            )}
                          </div>
                        )}
                      </li>
                    </>
                  )}
                </DeclarationWrapper>
              )}
            </ol>
          </div>
        </PageTemplate>
      )}
    </>
  );
};
